// ===== Packages =====

import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
}                               from 'react';
import styled                   from 'styled-components';
// eslint-disable-next-line import/no-extraneous-dependencies
import moment                   from 'moment-timezone';
import { ReactSVG }             from 'react-svg';
import {
    useParams,
    useMatch,
    useNavigate,
    useLocation,
    useSearchParams,
}                               from 'react-router-dom';
import {
    getFirestore,
    onSnapshot,
    doc,
    Unsubscribe,
}                               from 'firebase/firestore';
import {
    Editor,
    Descendant,
    Node,
    Transforms,
}                               from 'slate';
import {
    ref,
    getStorage,
    getDownloadURL,
}                               from 'firebase/storage';
import { v4 as uuidv4 }         from 'uuid';

// ===== Firebase =====

import fire                     from '../../../fire';

// ===== Components =====

import {
    AnnotationEditor,
}                               from '../editors';
import {
    Button,
    OptionsMenu,
    GenericModal,
}                               from '.';
import Spinner                  from '../../Spinner';
import Modal                    from '../../Modal';

// ===== Services =====

import {
    copyToClipboard,
    fontColorDiscriminator,
    recordUserAction,
    detectTouchDevice,
    formatNumber,
    playAudio,
    updateAnnotation,
    createAnnotation,
    getAnnotationCommentAuthorItems,
    deleteAnnotations,
    getUser,
    markNotificationAsRead,
    getAnnotationUnreadNotifications,
    getAnnotationComments,
    getAnnotationFreshnessTimestamp,
    isAnnotationComment,
    isAnnotationCommentTombstone,
    subscribeToAnnotationEngagement,
    subscribeToAnnotationEngagementItems,
    getPressEventHandlers,
    getTouchMouseEventHandlers,
    isTestMode,
} from '../../../services';
import buildAnnotationDictationReadyKey       from '../../../services/buildAnnotationDictationReadyKey';
import getOrCreateAudioClip                   from '../../../services/getOrCreateAudioClip';
import logAnnotationEditDebug                 from '../../../services/logAnnotationEditDebug';
import selectAnnotationDictationPlaybackPath  from '../../../services/selectAnnotationDictationPlaybackPath';

// ===== Hooks =====

import {
    useEventListener,
    useInterval,
    useLatest,
    useTimeout,
}                               from '../../../hooks';

// ===== Interfaces =====

import {
    IAnnotationItem,
    IAnnotationQuote,
    IAnnotationEngagementItem,
    IAnnotationValue,
    IDimension,
    IReadPostItem,
    ISnackbarItem,
    IPartialUserItem,
    IZoomLevelItem,
    INotificationItem,
    IModalMessage,
}                               from '../../../interfaces';

// ===== Enums =====

import {
    ANNOTATION_DICTATION_SOURCE,
    ANNOTATION_TARGET_SCOPE,
    AUTHENTICATION_TYPE,
    BUTTON_TYPE,
    CURSOR_TARGET,
    EDITOR_CONTEXT_TYPE,
    FIRESTORE_COLLECTION,
    INTERACTABLE_OBJECT,
    PAGE_ROUTE,
    STORAGE_ERROR_CODE,
    TOOLTIP_TYPE,
    USER_ACTION_TYPE,
}                               from '../../../enums';

// ===== Sounds =====

import AnnotationDelete         from '../../../sounds/delete.mp3';

// ===== Images =====

import SmileyIcon               from '../../../images/editor/smiley.svg';
import MoreIcon                 from '../../../images/editor/more.svg';
import CautionIcon              from '../../../images/caution.svg';
import InfoIcon                 from '../../../images/info.svg';
import LinkIcon                 from '../../../images/editor/link.svg';
import TrashIcon                from '../../../images/editor/trash.svg';
import PlayIcon                 from '../../../images/editor/play-fill.svg';
import PauseIcon                from '../../../images/editor/pause-fill.svg';
import CommentIcon              from '../../../images/comment.svg';
import HighlightIcon            from '../../../images/highlight.svg';
import SaveIcon                 from '../../../images/editor/floppy-disk.svg';
import RedoIcon                 from '../../../images/editor/redo.svg';

// ===== Constants =====

import {
    DEFAULT_CSS_TRANSITION_DURATION,
    HOVER_TARGET_CLASSNAME,
    ANNOTATION_EDITOR_Z_INDEX,
    BODY_FONT_SIZE,
    DEFAULT_EDITOR_COLOR,
    USER_PROFILE_AVATAR_LENGTH_MULTIPLIER,
    DEFAULT_AUDIO_VOLUME,
    DEFAULT_SNACKBAR_VISIBLE_DURATION,
    MILLISECONDS_IN_A_SECOND,
    ANNOTATION_FOCUSED_CLOSE_BUTTON_HEIGHT,
    ANNOTATION_EDITOR_FONT_MULTIPLIER_REGULAR,
    ANNOTATION_COMMENT_EDITOR_CONTAINER_PADDING,
    ANNOTATION_COMMENTS_CONTAINER_VERTICAL_MARGIN,
    ANNOTATION_DECORATION_PREFIX,
    MODAL_SHEET_CLOSE_BUTTON_CONTAINER_TOP,
    MODAL_SHEET_CLOSE_BUTTON_COLOR,
    ANNOTATION_MORE_BUTTON_LENGTH,
    EXTENDED_SNACKBAR_VISIBLE_DURATION,
    SIGN_IN_ANNOTATION_CREATE_MESSAGE,
    SUPPORT_EMAIL_ADDRESS,
    DEFAULT_API_PING_VERSION_VALUE,
    MAX_ANNOTATION_HEIGHT,
    USER_VERIFY_EMAIL_BUTTON_TEXT,
}                               from '../../../constants/generalConstants';
import {
    DELETE_ANNOTATION_MESSAGE,
}                               from '../../../constants/notificationMessages';
import CURSOR_SIGN              from '../../../constants/cursorSigns';
import MEDIA_QUERY_SIZE         from '../../../constants/mediaQuerySizes';
import FONT_TYPE                from '../../../constants/fontType';
import {
    ZOOM_LEVEL,
}                               from '../elements/enums';

// ===== Styles =====

import { theme as themeObj }    from '../../../themes/theme-context';
import {
    UserAvatar,
    PlaceholderBox,
    AvatarContainer,
}                               from '../../../styles';

// ===== Initialize Firebase =====

const { auth } = fire();
const annotationCommentsCache: Map<string, Map<string, IAnnotationItem>> = new Map();
const annotationCommentsFetchedCache: Set<string> = new Set();
const annotationCommentCountCache: Map<string, number> = new Map();

/**
 * Counts only non-tombstoned comments so collapsed indicators never include deleted replies.
 */
const getVisibleAnnotationCommentCount = (commentItems: Iterable<IAnnotationItem>): number => Array.from(commentItems)
    .filter((comment) => !isAnnotationCommentTombstone(comment)).length;

Annotation.defaultProps = {
    toggleAnnotationStack: undefined,
    setRemeasureBucketHeight: undefined,
    showAnnotationDetail: undefined,
    hideAnnotationDetail: undefined,
    userIsAdmin: false,
    hideUserSignature: false,
    isAnnotationAuthorComment: false,
    zIndex: undefined,
    handleEditComment: undefined,
    setIsUserProfileDialogExpanded: undefined,
    setNotifyUserToSignUpAfterAnnotationCreationAttempt: undefined,
    removeAnnotationLocally: undefined,
    setTargetAnnotationID: undefined,
    remeasureBucketHeight: undefined,
    trackViewOnFocus: true,
    enableRouteFocusSync: true,
    detachedAnnotationReattachTargetId: null,
    detachedAnnotationReattachInFlightId: null,
    detachedAnnotationPendingReattachQuote: undefined,
    startDetachedAnnotationReattach: undefined,
    confirmDetachedAnnotationReattach: undefined,
    clearDetachedAnnotationPendingQuote: undefined,
    cancelDetachedAnnotationReattach: undefined,
};
interface Props {
    customCursorActive: boolean,
    annotation: IAnnotationItem,
    user: IPartialUserItem | null,
    userIsAdmin?: boolean,
    userEmailVerified: boolean,
    post: IReadPostItem | undefined,
    hasSound: boolean,
    bucketWidth: number,
    bucketHeight: number | null,
    currentSessionId: string | null,
    index: number,
    zIndex?: number,
    horizontalMargin: number,
    annotationStackActive: boolean,
    annotationStackExpanded: boolean,
    annotationCount: number,
    hide: boolean,
    interactionDisabled: boolean,
    viewportDimensions: IDimension,
    postAuthorIds: string[],
    postUserItems: Map<string, IPartialUserItem>,
    postUserAvatarURLs: Map<string, string>,
    annotationAudioURLs: Map<string, string>,
    remeasureBucketHeight?: boolean,
    toggleAnnotationStack?: () => void,
    onCursorEnter: (
        targetType: CURSOR_TARGET | INTERACTABLE_OBJECT | string,
        actions: string[],
        candidateTarget?: HTMLElement,
    ) => void,
    onCursorLeave: (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
    setInputFocused: React.Dispatch<React.SetStateAction<boolean>>,
    setTargetAnnotationID?: React.Dispatch<React.SetStateAction<string | null>>,
    setCursorSigns: React.Dispatch<React.SetStateAction<string[]>>,
    showAnnotationDetail?: (annotation: IAnnotationItem) => void,
    hideAnnotationDetail?: (onlyClearAnnotationDetailData?: boolean) => void,
    setSnackbarData: React.Dispatch<React.SetStateAction<ISnackbarItem>>,
    setRemeasureBucketHeight?: React.Dispatch<React.SetStateAction<boolean>>,
    clearTimeoutAnnotationNudge: () => void,
    selectedPostValuePath: number[],
    currentZoomLevel: IZoomLevelItem | null,
    hideUserSignature?: boolean,
    isAnnotationAuthorComment?: boolean,
    handleEditComment?: (id: string | null) => void,
    setRefreshNotifications: React.Dispatch<React.SetStateAction<boolean>>,
    setIsUserProfileDialogExpanded?: (isExpanded: boolean) => void,
    setNotifyUserToSignUpAfterAnnotationCreationAttempt?: React.Dispatch<React.SetStateAction<boolean>>,
    removeAnnotationLocally?: (annotationId: string) => void,
    refreshAnnotation: ({ annotationId }: { annotationId: string }) => void,
    addToPostUserItems: ({ userItems }: { userItems: IPartialUserItem[] }) => Promise<void>,
    addToAnnotationAudioURLs: ({
        annotationId,
        url,
    }: {
        annotationId: string,
        url: string,
    }) => Promise<void>,
    removeFromAnnotationAudioURLs: ({ annotationId }: {annotationId: string }) => Promise<void>,
    trackViewOnFocus?: boolean,
    enableRouteFocusSync?: boolean,
    detachedAnnotationReattachTargetId?: string | null,
    detachedAnnotationReattachInFlightId?: string | null,
    detachedAnnotationPendingReattachQuote?: IAnnotationQuote | null,
    startDetachedAnnotationReattach?: (annotation: IAnnotationItem) => void,
    confirmDetachedAnnotationReattach?: () => Promise<void>,
    clearDetachedAnnotationPendingQuote?: () => void,
    cancelDetachedAnnotationReattach?: () => void,
}
function Annotation({
    customCursorActive,
    annotation,
    user,
    userIsAdmin = false,
    userEmailVerified,
    post,
    hasSound,
    bucketWidth,
    bucketHeight,
    currentSessionId,
    index,
    zIndex,
    horizontalMargin,
    annotationStackActive,
    annotationStackExpanded,
    annotationCount,
    hide,
    interactionDisabled,
    viewportDimensions,
    postAuthorIds,
    postUserItems,
    postUserAvatarURLs,
    annotationAudioURLs,
    remeasureBucketHeight,
    toggleAnnotationStack,
    onCursorEnter,
    onCursorLeave,
    setInputFocused,
    setTargetAnnotationID,
    setCursorSigns,
    showAnnotationDetail,
    hideAnnotationDetail,
    setSnackbarData,
    setRemeasureBucketHeight,
    clearTimeoutAnnotationNudge,
    selectedPostValuePath,
    currentZoomLevel,
    hideUserSignature,
    isAnnotationAuthorComment,
    handleEditComment,
    setRefreshNotifications,
    setIsUserProfileDialogExpanded,
    setNotifyUserToSignUpAfterAnnotationCreationAttempt,
    removeAnnotationLocally,
    refreshAnnotation,
    addToPostUserItems,
    addToAnnotationAudioURLs,
    removeFromAnnotationAudioURLs,
    trackViewOnFocus,
    enableRouteFocusSync,
    detachedAnnotationReattachTargetId,
    detachedAnnotationReattachInFlightId,
    detachedAnnotationPendingReattachQuote,
    startDetachedAnnotationReattach,
    confirmDetachedAnnotationReattach,
    clearDetachedAnnotationPendingQuote,
    cancelDetachedAnnotationReattach,
}: Props): JSX.Element {
    // ===== General Information =====
    //
    // 1. We specifically choose not to increment an annotation if a user loads the web app with an
    // annotation focused, and rather require that they click on the annotation to increment it.
    // This is to prevent the annotation from being incremented when the app rerenders when the
    // web app with an annotation focused.

    // ===== General Constants =====

    const AVATAR_LENGTH = 1.75 * BODY_FONT_SIZE;
    const ANNOTATION_STACK_OFFSET_INCREMENT_LENGTH = 0.4375 * BODY_FONT_SIZE;
    const OPTION_BUTTON_WIDTH = 5 * BODY_FONT_SIZE;
    const OPTION_BUTTON_HEIGHT = 1.875 * BODY_FONT_SIZE;
    const ANNOTATION_AUTHOR_PLACEHOLDER_WIDTH = 6.25 * BODY_FONT_SIZE;
    const ANNOTATION_AUTHOR_FONT_MULTIPLIER = 0.8;
    const QUOTE_CONTAINER_MARGIN_TOP = ANNOTATION_MORE_BUTTON_LENGTH + 0.3125 * BODY_FONT_SIZE;
    const DICTATION_BUTTON_LENGTH = ANNOTATION_MORE_BUTTON_LENGTH;
    const USER_SIGNATURE_PADDING = 0.5 * BODY_FONT_SIZE;
    const ANNOTATION_EDITOR_CONTAINER_PADDING_LEFT = 2.1875 * BODY_FONT_SIZE;
    // Minus two so that there's space between button and annotation content
    const DICTATION_BUTTON_X_OFFSET = USER_SIGNATURE_PADDING - 0.125 * BODY_FONT_SIZE;
    const MORE_BUTTON_X_OFFSET = DICTATION_BUTTON_X_OFFSET;
    const SNACKBAR_MESSAGE_COPY_LINK_SUCCESS = 'Copied annotation link to clipboard!';
    const SNACKBAR_MESSAGE_DELETE_ANNOTATION_SUCCESS = 'Annotation successfully deleted.';
    const SNACKBAR_MESSAGE_DELETE_COMMENT_SUCCESS = 'Comment successfully deleted.';
    const SNACKBAR_MESSAGE_COPY_LINK_ERROR = 'There was a problem copying annotation link to your clipboard';
    // Snackbar message when comment error occurs
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_COMMENT_CREATE_ERROR = `Encountered a problem creating comment. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`;
    // Snackbar message when annotation error occurs
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_DELETE_ANNOTATION_FAILURE = `Encountered a problem deleting annotation. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`;
    // Snackbar message when annotation error occurs
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_DELETE_COMMENT_FAILURE = `Encountered a problem deleting comment. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`;
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_UPDATE_ERROR = `Encountered a problem updating annotation. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`;
    const PING_REFRESH_DEBOUNCE_DURATION = 350;
    const COMMENTS_INITIAL_RENDER_COUNT = 12;
    const COMMENTS_RENDER_STEP_COUNT = 12;
    const COMMENT_FETCH_RETRY_BASE_DELAY_MS = 1.5 * MILLISECONDS_IN_A_SECOND;
    const COMMENT_FETCH_MAX_RETRY_COUNT = 3;
    const DETACHED_ANNOTATION_ACTION_CLICK_GUARD_DURATION_MS = 750;

    // ===== Refs =====

    const containerRef = useRef<HTMLDivElement | null>(null);
    const audioNodeRef = useRef<HTMLAudioElement>(null);
    const touchStartRef = useRef<{ x: number, y: number } | null>(null);
    const recordedFocusViewRef = useRef<boolean>(false);
    const lastDictationToggleTimestampRef = useRef<number>(0);
    const playDictationAfterFetchRef = useRef<boolean>(false);
    const localDictationSpeechRequestIdRef = useRef<number>(0);
    const localDictationBoundaryCharIndexRef = useRef<number>(-1);
    const localDictationUtteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
    const ignoreNextContainerClickRef = useRef<boolean>(false);
    const ignoreNextContainerClickTimeoutRef = useRef<number | null>(null);
    const annotationRefreshTimeoutRef = useRef<number | null>(null);
    const commentsRefreshTimeoutRef = useRef<number | null>(null);

    // ----- Sound Clips
    const deleteClip = useRef<HTMLAudioElement | null>(null);
    const missingCommentsRecoveryAttemptedRef = useRef<boolean>(false);
    const focusedCommentRefreshAttemptedRef = useRef<boolean>(false);

    // ===== React Router =====

    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();
    const annotationIsFocused = useMatch(`${PAGE_ROUTE.book}/:annotationId`);
    const [searchParams] = useSearchParams();

    // ===== State =====

    const [annotationIsVisible, setAnnotationIsVisible] = useState<boolean>(false);
    const [lastAnnotationPingVersion, setLastAnnotationPingVersion] = useState<number | undefined>(undefined);
    const [lastCommentThreadPingVersion, setLastCommentThreadPingVersion] = useState<number>(DEFAULT_API_PING_VERSION_VALUE);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
    const [annotationHeight, setAnnotationHeight] = useState<number | null>(null);
    const [annotationEditor, setAnnotationEditor] = useState<Editor | null>(null);
    const [annotationCommentEditor, setAnnotationCommentEditor] = useState<Editor | null>(null);
    const [remeasureAnnotationHeight, setRemeasureAnnotationHeight] = useState<boolean>(true);
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [dictationPlaying, setDictationPlaying] = useState<boolean>(false);
    const [startTime, setStartTime] = useState<number | null>(null);
    const [savedTime, setSavedTime] = useState<number | null>(null);
    const [audioLength, setAudioLength] = useState<number>(0);
    const [timeElapsed, setTimeElapsed] = useState<number>(0);
    const [annotationComments, setAnnotationComments] = useState<Map<string, IAnnotationItem>>(new Map());
    const [annotationEngagement, setAnnotationEngagement] = useState<IAnnotationEngagementItem | null>(null);
    const [commentEngagementItems, setCommentEngagementItems] = useState<Map<string, IAnnotationEngagementItem>>(new Map());
    const [renderedCommentCount, setRenderedCommentCount] = useState<number>(0);
    const [pendingCommentCreationIds, setPendingCommentCreationIds] = useState<Set<string>>(new Set());
    // Failed optimistic comment creates that should retry silently in background.
    const [queuedCommentRetries, setQueuedCommentRetries] = useState<Map<string, {
        postId: string,
        parentId: string,
        commentIndex: number,
        value: string,
        text: string,
        mediaIds: string[],
        zoomLevel: IZoomLevelItem['level'],
        chapterId?: string,
        sectionId?: string,
        draftStorageKey: string,
        attempt: number,
        nextRetryAt: number,
        inFlight: boolean,
    }>>(new Map());
    const [fetchingAnnotationComments, setFetchingAnnotationComments] = useState<boolean>(false);
    const [fetchedAnnotationComments, setFetchedAnnotationComments] = useState<boolean>(false);
    const [annotationCommentsRequestFailed, setAnnotationCommentsRequestFailed] = useState<boolean>(false);
    const [annotationCommentsRetryCount, setAnnotationCommentsRetryCount] = useState<number>(0);
    const [fetchingDictationAudio, setFetchingDictationAudio] = useState<boolean>(false);
    const annotationIsComment = isAnnotationComment(annotation);
    // Collapsed touch/mobile annotations are avatar-only tap targets. Keeping the
    // floating "more" button mounted there leaves its circular background
    // peeking into the text column beside the annotation shelf.
    const showAnnotationOptionsMenu = (
        viewportDimensions.width >= MEDIA_QUERY_SIZE.medium.min
        || isFocused
        || annotationIsComment
    );
    /**
     * Tracks the latest dictation asset revision we have already fetched.
     *
     * Annotation dictation regeneration can overwrite the same MP3 path, so a
     * plain file-path-count comparison is not enough to know when the browser
     * should fetch the refreshed asset again.
     */
    const [lastFetchedDictationReadyKey, setLastFetchedDictationReadyKey] = useState<string | null>(null);
    const [unreadNotifications, setUnreadNotifications] = useState<INotificationItem[] | null>(null);
    const [fetchedUnreadNotifications, setFetchedUnreadNotifications] = useState<boolean>(false);
    const [listenForAnnotationComments, setListenForAnnotationComments] = useState<boolean>(false);
    const [subscribedToCommentUpdates, setSubscribedToCommentUpdates] = useState<boolean>(false);
    const [listenForAnnotationUpdates, setListenForAnnotationUpdates] = useState<boolean>(false);
    const [subscribedToAnnotationUpdates, setSubscribedToAnnotationUpdates] = useState<boolean>(false);
    const [getDictationAttempts, setGetDictationAttempts] = useState<number>(0);
    const [optionsMenuIsVisible, setOptionsMenuIsVisible] = useState<boolean>(false);
    // Stores the state of the editor modal
    const [modal, setModal] = useState<{
        visible: boolean,
        message: IModalMessage,
        proceedCallback?:(e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
        rejectCallback?: (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
            }>({
                visible: false,
                message: {},
            });

    /**
     * Summarizes the latest saved annotation value so edit-mode logs can tell
     * whether the inline editor went blank because the content is empty or
     * because a populated Slate value failed to render.
     */
    const getSavedAnnotationValueSummary = useCallback((): {
        nodeCount: number | null,
        textLength: number | null,
        valueLength: number,
    } => {
        const latestValue = annotation.history[0]?.value || '';

        if (latestValue.length === 0) {
            return {
                nodeCount: 0,
                textLength: 0,
                valueLength: 0,
            };
        }

        try {
            const slateValue = JSON.parse(latestValue) as Descendant[];
            const slateRoot = { children: slateValue } as Editor;
            return {
                nodeCount: Array.isArray(slateValue) ? slateValue.length : null,
                textLength: Node.string(slateRoot).length,
                valueLength: latestValue.length,
            };
        } catch (error) {
            return {
                nodeCount: null,
                textLength: null,
                valueLength: latestValue.length,
            };
        }
    }, [annotation.history]);

    // ReaderView can temporarily keep a local optimistic id after ack.
    // Use canonicalId for backend reads/writes while preserving local id for UI keys.
    const annotationCanonicalId = (
        annotation as (IAnnotationItem & { canonicalId?: string })
    ).canonicalId || annotation.id;
    const annotationHasCanonicalBackendId = !(
        annotationCanonicalId.startsWith('pending-annotation-')
        || annotationCanonicalId.startsWith('pending-comment-')
    );

    /**
     * The latest browser-playable dictation revision for this annotation.
     *
     * The ready key only changes when the playable path changes or when the
     * backend produces a new transcript snapshot for the same path. That lets
     * the UI detect same-path MP3 overwrites after annotation edits.
     */
    const dictationReadyKey = useMemo(() => buildAnnotationDictationReadyKey({
        dictationFilePaths: annotation.dictationFilePaths,
        transcripts: annotation.transcripts,
    }), [
        annotation.dictationFilePaths,
        annotation.transcripts,
    ]);
    const hasAnnotationDictationPaths = useMemo(() => (
        annotationHasCanonicalBackendId
        && Array.isArray(annotation.dictationFilePaths)
        && annotation.dictationFilePaths.length > 0
    ), [
        annotation.dictationFilePaths,
        annotationHasCanonicalBackendId,
    ]);
    const annotationDictationText = useMemo(() => (
        (annotation.history[0]?.text || '').trim()
    ), [annotation.history]);
    const localDictationTranscript = useMemo(() => {
        const wordMatches = Array.from(annotationDictationText.matchAll(/\S+/g));
        const words = wordMatches
            .map((wordMatch) => {
                if (typeof wordMatch.index !== 'number') {
                    return null;
                }

                const startTimeMs = wordMatch.index;
                const endTimeMs = wordMatch.index + wordMatch[0].length;
                const timestampFromPseudoMs = (pseudoMs: number): {
                    seconds: string,
                    nanos: number,
                } => ({
                    seconds: String(Math.floor(pseudoMs / MILLISECONDS_IN_A_SECOND)),
                    nanos: (pseudoMs % MILLISECONDS_IN_A_SECOND) * 1000000,
                });

                return {
                    startTime: timestampFromPseudoMs(startTimeMs),
                    endTime: timestampFromPseudoMs(endTimeMs),
                    word: wordMatch[0],
                };
            })
            .filter((word): word is {
                startTime: { seconds: string, nanos: number },
                endTime: { seconds: string, nanos: number },
                word: string,
            } => word != null);

        if (words.length === 0) {
            return undefined;
        }

        return {
            data: [{
                alternatives: [{
                    transcript: annotationDictationText,
                    words,
                }],
            }],
            source: ANNOTATION_DICTATION_SOURCE.browserSpeechSynthesis,
        };
    }, [annotationDictationText]);
    const activeAnnotationTranscript = localDictationUtteranceRef.current != null
        ? localDictationTranscript
        : annotation.transcripts?.[0];
    const browserSpeechSynthesisIsAvailable = (
        typeof window !== 'undefined'
        && typeof window.speechSynthesis !== 'undefined'
        && typeof SpeechSynthesisUtterance !== 'undefined'
    );
    const canUseLocalDictationSpeech = (
        browserSpeechSynthesisIsAvailable
        && annotationDictationText.length > 0
    );
    const canPlayAnnotationDictation = (
        hasAnnotationDictationPaths
        || canUseLocalDictationSpeech
    );

    // ===== Animation Constants ======

    const ANNOTATION_TRANSITION_DURATION = DEFAULT_CSS_TRANSITION_DURATION / 2;
    const GET_DICTATION_TIMEOUT_DURATION = 5 * MILLISECONDS_IN_A_SECOND;
    const MEASURE_ANNOTATION_HEIGHT_TIMEOUT_DURATION = 0.5 * MILLISECONDS_IN_A_SECOND;
    const COMMENT_RETRY_BASE_DELAY_MS = 2000;
    const COMMENT_RETRY_MAX_DELAY_MS = 30000;
    const clientIsOnline = (): boolean => (
        typeof navigator === 'undefined' || navigator.onLine
    );
    const annotationIsDetached = (
        !annotationIsComment
        && annotation.detached?.detached === true
    );
    const annotationIsDetachedReattachTarget = (
        annotationIsDetached
        && (
            detachedAnnotationReattachTargetId === annotation.id
            || detachedAnnotationReattachTargetId === annotationCanonicalId
        )
    );
    const annotationDetachedReattachInFlight = (
        annotationIsDetached
        && (
            detachedAnnotationReattachInFlightId === annotation.id
            || detachedAnnotationReattachInFlightId === annotationCanonicalId
        )
    );
    const annotationHasPendingDetachedReattachQuote = (
        annotationIsDetachedReattachTarget
        && !!detachedAnnotationPendingReattachQuote
        && !!confirmDetachedAnnotationReattach
        && !!clearDetachedAnnotationPendingQuote
    );
    const canRepairDetachedAnnotation = (
        annotationIsDetached
        && !!startDetachedAnnotationReattach
        && !!cancelDetachedAnnotationReattach
    );
    /**
     * Detached annotations still need to show their last saved quote even when
     * they are not focused, because that stale quote is the repair context.
     */
    const latestAnnotationQuoteText = useMemo((): string | null => {
        const latestQuote = annotation.quoteHistory[0];
        if (!latestQuote || latestQuote.texts.length === 0) {
            return null;
        }

        return latestQuote.texts.reduce((acc: string, text: string): string => {
            if (acc.length === 0) {
                return text;
            }

            return `${acc}, ${text}`;
        }, '');
    }, [
        annotation.quoteHistory,
    ]);
    const shouldRenderAnnotationQuote = (
        !annotation.comment
        && !annotationIsComment
        && !!latestAnnotationQuoteText
        && (
            isFocused
            || annotationIsDetached
        )
    );
    const annotationQuoteIsInteractive = (
        shouldRenderAnnotationQuote
        && isFocused
        && !annotationIsDetached
    );
    /**
     * Gives the confirmation state a compact preview of the staged
     * replacement quote without flooding the detached annotation card.
     */
    const detachedAnnotationPendingReattachPreviewText = useMemo((): string | null => {
        if (!annotationHasPendingDetachedReattachQuote) {
            return null;
        }

        const previewText = detachedAnnotationPendingReattachQuote.texts
            .join(' ')
            .replace(/\s+/g, ' ')
            .trim();

        if (!previewText) {
            return null;
        }

        return previewText.length > 140
            ? `${previewText.slice(0, 137)}...`
            : previewText;
    }, [
        annotationHasPendingDetachedReattachQuote,
        detachedAnnotationPendingReattachQuote,
    ]);
    let detachedAnnotationNoticeText = 'This annotation is detached from current post text and hidden from readers.';
    if (annotationHasPendingDetachedReattachQuote) {
        detachedAnnotationNoticeText = 'Confirm reattaching this annotation to the selected text, or choose another passage first.';
    } else if (annotationIsDetachedReattachTarget) {
        detachedAnnotationNoticeText = 'Select replacement text in the post to reattach this annotation.';
    }
    /**
     * Detached repair buttons update local card state on press. Prevent the
     * browser from retargeting the follow-up click onto the annotation card
     * after rerender, otherwise the card expands when repair starts.
     */
    const handleDetachedAnnotationActionPress = useCallback((
        e: React.MouseEvent | React.TouchEvent | React.PointerEvent,
    ): void => {
        ignoreNextContainerClickRef.current = true;
        if (ignoreNextContainerClickTimeoutRef.current != null) {
            window.clearTimeout(ignoreNextContainerClickTimeoutRef.current);
        }
        ignoreNextContainerClickTimeoutRef.current = window.setTimeout(() => {
            ignoreNextContainerClickRef.current = false;
            ignoreNextContainerClickTimeoutRef.current = null;
        }, DETACHED_ANNOTATION_ACTION_CLICK_GUARD_DURATION_MS);
        e.preventDefault();
        e.stopPropagation();
    }, []);
    const handleConfirmDetachedAnnotationReattach = useCallback((): void => {
        if (!confirmDetachedAnnotationReattach) {
            return;
        }

        confirmDetachedAnnotationReattach().catch(() => undefined);
    }, [
        confirmDetachedAnnotationReattach,
    ]);
    const buildCommentEquivalenceKey = (commentItem: IAnnotationItem): string | null => {
        if (
            !commentItem.comment
            || commentItem.comment.parentId == null
            || commentItem.comment.index == null
        ) {
            return null;
        }
        const text = commentItem.history[0]?.text || '';
        return `${commentItem.userId}::${commentItem.comment.parentId}::${commentItem.comment.index}::${text}`;
    };
    const getHighestCommentIndex = (commentItems: Iterable<IAnnotationItem>): number => Array.from(commentItems)
        .reduce((maxIndex, commentItem) => {
            if (
                !commentItem.comment
                || commentItem.comment.index == null
            ) {
                return maxIndex;
            }

            return Math.max(maxIndex, commentItem.comment.index);
        }, -1);

    /**
     * Clears transient failure bookkeeping after any successful comment sync.
     */
    const clearAnnotationCommentRecoveryState = useCallback((): void => {
        setAnnotationCommentsRequestFailed(false);
        setAnnotationCommentsRetryCount(0);
        setFetchingAnnotationComments(false);
    }, []);

    /**
     * Records a transient comment-load failure without promoting it to an authoritative empty thread.
     */
    const handleAnnotationCommentFetchFailure = useCallback((): void => {
        setAnnotationCommentsRequestFailed(true);
        setAnnotationCommentsRetryCount((prev) => prev + 1);
        setFetchingAnnotationComments(false);
    }, []);

    /**
     * Protects the visible thread from overlapping fetches that briefly return
     * fewer comments than a response we already rendered.
     */
    const mergeFetchedAnnotationComments = useCallback((
        fetchedCommentMap: Map<string, IAnnotationItem>,
        localCommentMap: Map<string, IAnnotationItem>,
        currentCommentEngagementItems: Map<string, IAnnotationEngagementItem>,
    ): Map<string, IAnnotationItem> => {
        const mergedComments = new Map(fetchedCommentMap);
        const serverReturnedFewerComments = fetchedCommentMap.size < localCommentMap.size;

        localCommentMap.forEach((localComment, localCommentId) => {
            const serverComment = fetchedCommentMap.get(localCommentId);
            if (serverComment) {
                const commentUpdatedAt = currentCommentEngagementItems.get(localCommentId)?.updatedAt;
                const localFreshness = getAnnotationFreshnessTimestamp(localComment, commentUpdatedAt);
                const serverFreshness = getAnnotationFreshnessTimestamp(serverComment, commentUpdatedAt);
                if (localFreshness > serverFreshness) {
                    mergedComments.set(localCommentId, localComment);
                }
                return;
            }

            if (
                localCommentId.startsWith('pending-comment-')
                || serverReturnedFewerComments
            ) {
                mergedComments.set(localCommentId, localComment);
            }
        });

        return mergedComments;
    }, []);

    /**
     * Merges freshly fetched server comments into local state without dropping
     * optimistic pending comments that have not been acknowledged yet.
     */
    const applyFetchedAnnotationComments = useCallback((commentMap: Map<string, IAnnotationItem>): void => {
        setAnnotationComments((prev) => mergeFetchedAnnotationComments(
            commentMap,
            prev,
            commentEngagementItems,
        ));
        setPendingCommentCreationIds((prev) => {
            if (prev.size === 0) return prev;
            const nextPendingCommentIds = new Set(prev);
            prev.forEach((pendingCommentId) => {
                if (commentMap.has(pendingCommentId)) {
                    nextPendingCommentIds.delete(pendingCommentId);
                }
            });
            return nextPendingCommentIds.size === prev.size
                ? prev
                : nextPendingCommentIds;
        });
    }, [
        commentEngagementItems,
        mergeFetchedAnnotationComments,
    ]);

    // ===== Methods =====

    const handleVisibility = (isVisible: boolean): void => {
        if (
            !annotation.comment
            && !annotationIsComment
            && !isFocused
            && isVisible
            && !unreadNotifications
            && !fetchedUnreadNotifications
            && isAnnotationAuthor
            && !annotationCanonicalId.startsWith('pending-')
            && (
                !annotationStackActive
                || (
                    annotationStackActive
                    && !annotationStackExpanded
                    && index === 0
                ) || (
                    annotationStackActive
                    && annotationStackExpanded
                )
            )
        ) {
            fetchUnreadNotifications();
        }
        if (!listenForAnnotationUpdates) {
            setListenForAnnotationUpdates(true);
        }
        setAnnotationIsVisible(isVisible);
    };

    useEffect(() => {
        const node = containerRef.current;
        if (!node) return undefined;
        if (typeof IntersectionObserver === 'undefined') {
            handleVisibility(true);
            return undefined;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const isVisible = !!entries[0]?.isIntersecting;
                handleVisibility(isVisible);
            },
            {
                threshold: 0,
            },
        );

        observer.observe(node);

        return () => {
            observer.disconnect();
        };
    }, [
        annotation.id,
        handleVisibility,
    ]);

    const onMouseEnter = (e: React.MouseEvent): void => {
        onCursorLeave(e);
        let cursorSign = CURSOR_SIGN.click;
        if (
            annotationStackActive
            && !annotationStackExpanded
            && annotationHeight
            && annotationHeight > MAX_ANNOTATION_HEIGHT
        ) {
            cursorSign = CURSOR_SIGN.more;
        } else if (
            annotationStackActive
            && !annotationStackExpanded
            && index === 0
        ) {
            cursorSign = CURSOR_SIGN.expandStack;
        }

        if (!isFocused) {
            onCursorEnter(
                CURSOR_TARGET.annotation,
                [cursorSign],
                e.currentTarget as HTMLElement,
            );
        }
        if (setTargetAnnotationID) setTargetAnnotationID(annotation.id);
    };

    const onMouseLeave = (e: React.MouseEvent): void => {
        if (setTargetAnnotationID) setTargetAnnotationID(null);
        onCursorLeave(e);
    };

    /**
     * Keeps the local menu-open state in sync with the actual options menu.
     *
     * Annotation focus clicks are disabled while the menu is visible so button
     * presses do not immediately reopen/close the card. That state must also
     * clear when the menu dismisses, otherwise the card becomes permanently
     * non-focusable after actions like Edit.
     */
    const onOptionsMenuToggle = (visible: boolean): void => {
        setOptionsMenuIsVisible(visible);
    };

    const onButtonMouseEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
        onCursorEnter(
            CURSOR_TARGET.annotationElement,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onButtonMouseLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    /**
     * Retrieves user items of annotation author
     */
    const fetchAnnotationAuthorLatest = useLatest({
        postUserItems,
    });
    const fetchAnnotationAuthor = (): void => {
        if (!clientIsOnline()) return;
        getUser({
            test: isTestMode(),
            userId: annotation.userId,
        }).then((userItem) => {
            const cur = fetchAnnotationAuthorLatest.current;
            if (
                userItem != null
                && !cur.postUserItems.get(userItem.id)
            ) {
                addToPostUserItems({
                    userItems: [userItem],
                });
            }
        }).catch(() => {
            // Best-effort fetch; avoid noisy offline/internal surfacing during scroll visibility updates.
        });
    };

    /**
     * Retrieves user items of all the annotation commenters
     */
    const fetchAnnotationCommentAuthorsLatest = useLatest({
        postUserItems,
    });
    const fetchAnnotationCommentAuthors = (): void => {
        if (!clientIsOnline()) return;
        getAnnotationCommentAuthorItems({
            test: isTestMode(),
            parentAnnotationId: annotationCanonicalId,
        }).then((commentAuthorItems) => {
            const cur = fetchAnnotationCommentAuthorsLatest.current;
            const mergedCommentAuthorItems: IPartialUserItem[] = [];
            commentAuthorItems.forEach((author) => {
                const existingAuthor = cur.postUserItems.get(author.id);
                mergedCommentAuthorItems.push({
                    ...existingAuthor,
                    ...author,
                    photoURL: author.photoURL || existingAuthor?.photoURL,
                    avatarFilePath: author.avatarFilePath || existingAuthor?.avatarFilePath,
                });
            });
            if (mergedCommentAuthorItems.length > 0) {
                addToPostUserItems({
                    userItems: mergedCommentAuthorItems,
                });
            }
        }).catch(() => {
            // Best-effort fetch; avoid noisy offline/internal surfacing during scroll visibility updates.
        });
    };

    const focusAnnotation = (): void => {
        navigate(
            `/${PAGE_ROUTE.book}/${annotation.id}?${searchParams.toString()}`,
            {
                state: location.state,
            },
        );
    };

    const unfocusAnnotation = (): void => {
        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.closeAnnotation,
                sessionId: currentSessionId,
                payload: {
                    annotationId: annotation.id,
                },
            });
        }

        // Close button automatically disappears before unlocking cursor
        onCursorLeave();

        navigate(
            `/${PAGE_ROUTE.book}?${searchParams.toString()}`,
            {
                state: location.state,
            },
        );
    };

    const onClick = (e?: React.MouseEvent | React.TouchEvent): void => {
        if (ignoreNextContainerClickRef.current) {
            ignoreNextContainerClickRef.current = false;
            if (ignoreNextContainerClickTimeoutRef.current != null) {
                window.clearTimeout(ignoreNextContainerClickTimeoutRef.current);
                ignoreNextContainerClickTimeoutRef.current = null;
            }
            return;
        }
        if (
            e?.target instanceof Element
            && e.target.closest('[data-annotation-interactive="true"]')
        ) {
            return;
        }
        if (
            toggleAnnotationStack
            && !annotationStackExpanded
            && !annotationIsFocused
        ) {
            toggleAnnotationStack();
            // We want the sign to change immediately
            setCursorSigns([CURSOR_SIGN.click]);
        } else if (
            !annotationIsFocused
            || (
                !!annotationIsFocused
                && params.annotationId
                && params.annotationId !== annotation.id
            )
        ) {
            focusAnnotation();
            onCursorLeave(); // reset cursor once you open focus annotation
        }
    };

    const isInteractiveTouchTarget = (target: EventTarget | null): boolean => {
        if (!(target instanceof Element)) {
            return false;
        }

        return !!target.closest(
            'button, a, input, textarea, select, [role="button"], [role="textbox"],'
            + ' [contenteditable="true"], [data-slate-editor="true"], [data-annotation-interactive="true"]',
        );
    };

    const onTouchStart = (e: React.TouchEvent): void => {
        if (isInteractiveTouchTarget(e.target)) {
            touchStartRef.current = null;
            return;
        }

        const touch = e.touches[0];
        if (!touch) return;
        touchStartRef.current = {
            x: touch.clientX,
            y: touch.clientY,
        };
    };

    const onTouchEnd = (e: React.TouchEvent): void => {
        if (isInteractiveTouchTarget(e.target)) {
            touchStartRef.current = null;
            return;
        }

        const touch = e.changedTouches[0];
        const touchStart = touchStartRef.current;
        touchStartRef.current = null;
        if (!touch || !touchStart) return;

        const movedX = Math.abs(touch.clientX - touchStart.x);
        const movedY = Math.abs(touch.clientY - touchStart.y);
        const TAP_THRESHOLD_PX = 10;
        const isTap = movedX <= TAP_THRESHOLD_PX && movedY <= TAP_THRESHOLD_PX;
        if (!isTap) return;

        e.preventDefault();
        onClick(e);
    };

    const onEditAnnotation = (): void => {
        logAnnotationEditDebug('annotation-read-edit-requested', {
            annotationId: annotation.id,
            historyLength: annotation.history.length,
            isFocused,
            isComment: !!annotation.comment && annotationIsComment,
            hasAnnotationEditor: !!annotationEditor,
            ...getSavedAnnotationValueSummary(),
        });

        /**
         * Read-mode annotation cards keep a mounted read-only editor instance.
         * When we switch that instance into edit mode, proactively rehydrate it
         * from the latest saved history entry so the editable shell never opens
         * against stale or transiently empty Slate children.
         */
        if (annotationEditor) {
            try {
                const slateValue = JSON.parse(annotation.history[0].value) as Descendant[];
                if (Array.isArray(slateValue) && slateValue.length > 0) {
                    annotationEditor.children = slateValue;
                    annotationEditor.onChange();
                    const endPoint = Editor.end(annotationEditor, []);
                    Transforms.select(annotationEditor, endPoint);
                    logAnnotationEditDebug('annotation-read-edit-rehydrated-editor', {
                        annotationId: annotation.id,
                        editorNodeCount: annotationEditor.children.length,
                        editorTextLength: Node.string(annotationEditor).length,
                        slateValueNodeCount: slateValue.length,
                    });
                }
            } catch (error) {
                // Best-effort editor hydration only. If parsing fails, keep the
                // existing editor contents instead of blocking edit mode.
                logAnnotationEditDebug('annotation-read-edit-rehydrate-failed', {
                    annotationId: annotation.id,
                    error: error instanceof Error ? error.message : String(error),
                });
            }
        }

        setIsEditing(true);

        // we tell parent annotation editing has begun
        if (
            handleEditComment
            && !!annotation.comment
            && annotationIsComment
        ) {
            handleEditComment(annotation.id);
        }

        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.editInitiateAnnotation,
                sessionId: currentSessionId,
                payload: {
                    annotationId: annotation.id,
                },
            });
        }
    };

    /* We intentionally do not remove associations of deleted annotations
    to users or posts */
    const onDeleteAnnotationLatest = useLatest({
        currentSessionId,
    });
    const onDeleteAnnotation = (): void => {
        if (post && user) {
            // If annotation is visible, change URL
            if (
                !!annotationIsFocused
                && params.annotationId
                && params.annotationId === annotation.id
                && isFocused
            ) {
                navigate(
                    `/${PAGE_ROUTE.book}?${searchParams.toString()}`,
                    {
                        state: location.state,
                    },
                );
            }
            // Remove annotation locally for latency reasons
            if (
                !annotation.comment
                && !annotationIsComment
                && removeAnnotationLocally
            ) {
                removeAnnotationLocally(annotation.id);
            } else if (
                !!annotation.comment
                && annotationIsComment
                && removeAnnotationLocally
            ) {
                // For nested comment annotations, delete through parent thread state.
                removeAnnotationLocally(annotation.id);
            } else if (
                !!annotation.comment
                && annotationIsComment
            ) {
                // Delete locally from comment array
                const updatedAnnotationComments = new Map([
                    ...Array.from(annotationComments),
                ]);
                if (updatedAnnotationComments.get(annotation.id)) {
                    const removed = updatedAnnotationComments.delete(annotation.id);
                    if (!removed) {
                        setSnackbarData({
                            visible: true,
                            duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                            text: SNACKBAR_MESSAGE_DELETE_COMMENT_FAILURE,
                            icon: HighlightIcon,
                            hasFailure: true,
                        });
                        return;
                    }
                    setPendingCommentCreationIds((prev) => {
                        if (!prev.has(annotation.id)) return prev;
                        const updated = new Set(prev);
                        updated.delete(annotation.id);
                        return updated;
                    });
                    setAnnotationComments(updatedAnnotationComments);
                }
            }
            // Play Sound
            if (hasSound) {
                const audioClip = getOrCreateAudioClip({
                    audioRef: deleteClip,
                    src: AnnotationDelete,
                    volume: DEFAULT_AUDIO_VOLUME,
                });
                audioClip.pause();
                audioClip.currentTime = 0;
                playAudio(audioClip);
            }

            // Let user know the operation happened
            // If there's an error, we're let them know
            // after deletion on server attempted
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: !!annotation.comment && annotationIsComment
                    ? SNACKBAR_MESSAGE_DELETE_COMMENT_SUCCESS
                    : SNACKBAR_MESSAGE_DELETE_ANNOTATION_SUCCESS,
                icon: TrashIcon,
                hasSuccess: true,
            });

            deleteAnnotations({
                test: isTestMode(),
                postId: post.id,
                deleteAnnotationIds: [annotationCanonicalId],
            }).then((success) => {
                const cur = onDeleteAnnotationLatest.current;
                if (success) {
                    clearTimeoutAnnotationNudge();
                    if (cur.currentSessionId) {
                        // Record user action
                        recordUserAction({
                            test: isTestMode(),
                            type: USER_ACTION_TYPE.deleteAnnotation,
                            sessionId: cur.currentSessionId,
                            payload: {
                                annotationId: annotation.id,
                            },
                        });
                    }
                } else {
                    setSnackbarData({
                        visible: true,
                        duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                        text: !!annotation.comment && annotationIsComment
                            ? SNACKBAR_MESSAGE_DELETE_COMMENT_FAILURE
                            : SNACKBAR_MESSAGE_DELETE_ANNOTATION_FAILURE,
                        icon: TrashIcon,
                        hasFailure: true,
                    });
                }
            }).catch(() => {
                // Best-effort delete; local optimistic state already updated.
            });
        }
    };

    const onGetLink = (): void => {
        // Copy link to clipboard
        const link = `${window.location.origin}/${PAGE_ROUTE.book}/${annotation.id}`;
        copyToClipboard(
            link,
            () => {
                setSnackbarData({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: SNACKBAR_MESSAGE_COPY_LINK_SUCCESS,
                    icon: LinkIcon,
                });
            },
            () => {
                setSnackbarData({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: SNACKBAR_MESSAGE_COPY_LINK_ERROR,
                    icon: CautionIcon,
                    hasFailure: true,
                });
            },
        );

        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.getLinkAnnotation,
                sessionId: currentSessionId,
                payload: {
                    annotationId: annotation.id,
                },
            });
        }
    };

    const remeasureParentHeight = (): void => {
        if (setRemeasureBucketHeight) setRemeasureBucketHeight(true);
    };

    const toggleDictationLatest = useLatest({
        currentSessionId,
    });
    /**
     * Stops browser-local annotation speech and clears the active utterance.
     */
    const stopLocalDictationSpeech = (): void => {
        localDictationSpeechRequestIdRef.current += 1;
        localDictationBoundaryCharIndexRef.current = -1;
        localDictationUtteranceRef.current = null;

        if (!browserSpeechSynthesisIsAvailable) {
            return;
        }

        window.speechSynthesis.cancel();
    };

    /**
     * Reads the annotation text with browser speech synthesis when generated
     * dictation audio is unavailable. Browsers that emit speech boundary events
     * also get best-effort word highlighting while Storage/Cloud Run catches up.
     */
    const startLocalDictationSpeech = (): void => {
        if (
            !canUseLocalDictationSpeech
            || dictationPlaying
        ) {
            return;
        }

        stopLocalDictationSpeech();

        const requestId = localDictationSpeechRequestIdRef.current + 1;
        localDictationSpeechRequestIdRef.current = requestId;
        localDictationBoundaryCharIndexRef.current = -1;
        const utterance = new SpeechSynthesisUtterance(annotationDictationText);
        const clearIfCurrent = (): void => {
            if (localDictationSpeechRequestIdRef.current !== requestId) {
                return;
            }

            localDictationUtteranceRef.current = null;
            setDictationPlaying(false);
            setStartTime(null);
            setTimeElapsed(0);
            setSavedTime(null);
            recordFinishDictationAction();
        };

        utterance.onend = clearIfCurrent;
        utterance.onerror = clearIfCurrent;
        utterance.onboundary = (event: SpeechSynthesisEvent): void => {
            if (
                localDictationSpeechRequestIdRef.current !== requestId
                || typeof event.charIndex !== 'number'
                || !Number.isFinite(event.charIndex)
                || event.charIndex < localDictationBoundaryCharIndexRef.current
            ) {
                return;
            }

            // Boundary events are not universally supported, but when they are
            // available their character index can drive the existing dictation
            // highlighter through the synthetic transcript above. Some engines
            // emit sentence-level boundaries after word boundaries, so ignore
            // backwards jumps to avoid moving the highlight to earlier words.
            localDictationBoundaryCharIndexRef.current = event.charIndex;
            setTimeElapsed(Math.max(0, event.charIndex));
        };
        localDictationUtteranceRef.current = utterance;
        setDictationPlaying(true);
        setStartTime(Date.now());
        setSavedTime(null);
        setAudioLength(annotationDictationText.length);
        setTimeElapsed(0);

        const cur = toggleDictationLatest.current;
        if (cur.currentSessionId) {
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.startAnnotationDictation,
                sessionId: cur.currentSessionId,
                payload: {
                    id: annotation.id,
                    source: ANNOTATION_DICTATION_SOURCE.browserSpeechSynthesis,
                },
            });
        }

        window.speechSynthesis.speak(utterance);
    };

    /**
     * Starts playback after the dictation URL is already available.
     * The first button press may have been spent fetching the Storage URL, so
     * this keeps that original user intent from disappearing into a spinner.
     */
    const startDictationPlayback = (): void => {
        if (dictationPlaying || !audioNodeRef.current) {
            return;
        }

        audioNodeRef.current.play()
            .then(async () => {
                const cur = toggleDictationLatest.current;
                setDictationPlaying(true);
                // Cache timestamp started recording
                setStartTime(Date.now());

                if (cur.currentSessionId) {
                    // Record user action
                    recordUserAction({
                        test: isTestMode(),
                        type: USER_ACTION_TYPE.startAnnotationDictation,
                        sessionId: cur.currentSessionId,
                        payload: {
                            id: annotation.id,
                        },
                    });
                }
            })
            .catch((err) => {
                if (canUseLocalDictationSpeech) {
                    startLocalDictationSpeech();
                    return;
                }

                throw err;
            });
    };

    const toggleDictation = (e: React.MouseEvent | React.TouchEvent): void => {
        e.stopPropagation();
        const now = Date.now();
        if (now - lastDictationToggleTimestampRef.current < 250) {
            return;
        }
        lastDictationToggleTimestampRef.current = now;
        if (dictationPlaying) {
            stopLocalDictationSpeech();

            if (audioNodeRef.current) {
                audioNodeRef.current.pause();
            }

            setDictationPlaying(false);
            if (currentSessionId) {
                // Record user action
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.stopAnnotationDictation,
                    sessionId: currentSessionId,
                    payload: {
                        id: annotation.id,
                    },
                });
            }
            return;
        }
        if (!audioURL && hasAnnotationDictationPaths && !fetchingDictationAudio) {
            playDictationAfterFetchRef.current = true;
            getDictationAudio();
            return;
        }
        if (!audioURL && !hasAnnotationDictationPaths && canUseLocalDictationSpeech) {
            startLocalDictationSpeech();
            return;
        }
        // Updates timestamps on a regular interval
        if (!dictationPlaying && audioNodeRef.current) {
            // if (timeElapsed === 0) {
            //     audioNodeRef.current.currentTime = 0;
            // }
            startDictationPlayback();
        }
    };

    const computeTimeElapsed = (): number => {
        // Collect and save elapsed time
        // We are likely recording audio, so need to time it the elapsed time
        let currentTime = Date.now();
        if (
            dictationPlaying
            && audioLength > 0
            && audioNodeRef.current
            && startTime
        ) {
            // We have track data, so compute normally
            currentTime = (audioNodeRef.current.currentTime * MILLISECONDS_IN_A_SECOND) + startTime;
        }

        let difference;
        if (
            !dictationPlaying
            && timeElapsed > 0
            && startTime
        ) {
            difference = currentTime - startTime;
        } else if (savedTime && startTime) {
            // If we've paused, factor in previous time
            difference = (currentTime - startTime) + savedTime;
        } else {
            // Ternary makes sure that we don't begin with a large
            // timestamp
            difference = startTime ? currentTime - startTime : 0;
        }
        return difference;
    };

    const recordFinishDictationAction = (): void => {
        if (currentSessionId) {
            // Record user action
            const actionId = uuidv4();
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.finishAnnotationDictation,
                actionId,
                sessionId: currentSessionId,
                payload: {
                    id: annotation.id,
                },
            });
            if (!annotationHasCanonicalBackendId) {
                return;
            }
            updateAnnotation({
                test: isTestMode(),
                annotationId: annotationCanonicalId,
                playsToAdd: [actionId],
            }).catch(() => {
                // Best-effort telemetry write; optimistic annotations may not
                // have a backend row yet.
            });
        }
    };

    const scrollQuoteIntoView = (): void => {
        if (isFocused) {
            const highlightElement: HTMLElement | null = document
                .querySelector(`[data-${ANNOTATION_DECORATION_PREFIX}${annotation.id}='${annotation.id}']`);
            if (highlightElement) {
                highlightElement.scrollIntoView({
                    behavior: 'smooth',
                    inline: 'nearest',
                    block: 'center',
                });
            }
        }
    };

    const handleEditAnnotationLatest = useLatest({
        currentSessionId,
    });
    const handleEditAnnotation = (
        value: string,
        text: string,
        media: string[],
    ): void => {
        if (!annotationEditor) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: `There was a problem editing annotation. Editor reference not found. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                    },
                });
            }
            // eslint-disable-next-line max-len
            throw Error(`There was a problem editing annotation. Editor reference not found. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
        }
        if (!user) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: `There was a problem editing annotation. User data not found. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                    },
                });
            }
            // eslint-disable-next-line max-len
            throw Error(`There was a problem editing annotation. User data not found. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
        }
        // Button automatically disappears before unlocking cursor
        onCursorLeave();
        // we tell parent annotation editing is complete
        if (
            handleEditComment
            && !!annotation.comment
            && annotationIsComment
        ) {
            handleEditComment(null);
        }

        const timestamp = Date.now();
        const annotationValue: IAnnotationValue = {
            id: uuidv4(),
            timestamp,
            value,
            text,
        };
        setIsUpdating(true);
        // Edited annotation text regenerates dictation audio, so clear any
        // cached URL under both the local and canonical ids first.
        removeFromAnnotationAudioURLs({ annotationId: annotation.id });
        if (annotationCanonicalId !== annotation.id) {
            removeFromAnnotationAudioURLs({ annotationId: annotationCanonicalId });
        }
        setIsEditing(false);
        // We capture text sychronously because it is cleared by the annotation editor
        // after this call.
        // Placing it after this async function will result in an empty string.
        updateAnnotation({
            test: isTestMode(),
            annotationId: annotationCanonicalId,
            historyToAdd: [annotationValue],
            mediaIds: media,
        }).then((success) => {
            const cur = handleEditAnnotationLatest.current;
            setIsUpdating(false);
            if (!success) {
                setSnackbarData({
                    visible: true,
                    duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                    text: SNACKBAR_MESSAGE_UPDATE_ERROR,
                    icon: HighlightIcon,
                    hasFailure: true,
                });
                return;
            }
            if (cur.currentSessionId) {
                // Record user action
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.editSaveAnnotation,
                    sessionId: cur.currentSessionId,
                    payload: {
                        annotationId: annotation.id,
                    },
                });
            }
        });
    };

    const markCommentDeletedLocally = useCallback((commentId: string): void => {
        setAnnotationComments((prev) => {
            const updated = new Map([
                ...Array.from(prev),
            ]);
            const localComment = updated.get(commentId);
            if (!localComment) return prev;
            updated.set(commentId, {
                ...localComment,
                deleted: {
                    deleted: true,
                    timestamp: Date.now(),
                    deletedBy: user?.id || null,
                },
            });
            return updated;
        });
        setPendingCommentCreationIds((prev) => {
            if (!prev.has(commentId)) return prev;
            const updated = new Set(prev);
            updated.delete(commentId);
            return updated;
        });
    }, []);

    const handleCancelEditAnnotation = (): void => {
        setIsEditing(false);
        // we tell parent annotation editing is complete
        if (
            handleEditComment
            && !!annotation.comment
            && annotationIsComment
        ) {
            handleEditComment(null);
        }
        // Button automatically disappears before unlocking cursor
        onCursorLeave();

        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.editCancelAnnotation,
                sessionId: currentSessionId,
                payload: {
                    annotationId: annotation.id,
                },
            });
        }
    };

    /**
     * Submits an annotation to Firestore
     * @param value serialized annotation
     * @param text annotation text
     * @param media optional media associated with annotation
     */
    const handleSubmitAnnotationCommentLatest = useLatest({
        currentSessionId,
    });
    const queuedCommentRetriesLatest = useLatest({
        queuedCommentRetries,
        currentSessionId,
    });
    const handleSubmitAnnotationComment = (
        value: string,
        text: string,
        media: string[],
    ): boolean => {
        const showCommentCreateErrorSnackbar = (): void => {
            // Defer to next tick so it isn't swallowed by synchronous editor state updates.
            setTimeout(() => {
                setSnackbarData({
                    visible: true,
                    duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                    text: SNACKBAR_MESSAGE_COMMENT_CREATE_ERROR,
                    icon: HighlightIcon,
                    hasFailure: true,
                });
            }, 0);
        };

        if (!user) {
            setSnackbarData({
                visible: true,
                duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                text: SIGN_IN_ANNOTATION_CREATE_MESSAGE,
                icon: HighlightIcon,
                hasFailure: true,
            });
            if (setNotifyUserToSignUpAfterAnnotationCreationAttempt) {
                setNotifyUserToSignUpAfterAnnotationCreationAttempt(true);
            }
            return false;
        }
        if (!annotation.postId) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: `There was a problem creating annotation. Post ID not found. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                    },
                });
            }
            // eslint-disable-next-line max-len
            throw Error(`There was a problem creating annotation. Post ID not found. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
        }
        const annotationPostId = annotation.postId;
        const zoomLevel = currentZoomLevel?.level ?? ZOOM_LEVEL.five;

        // Shouldn't ever get here
        // In the event it does prevent anonymous user from creating an annotation
        if (
            auth.currentUser
            && auth.currentUser.isAnonymous
        ) {
            setSnackbarData({
                visible: true,
                duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                text: SIGN_IN_ANNOTATION_CREATE_MESSAGE,
                icon: HighlightIcon,
                hasFailure: true,
            });
            return false;
        }
        if (
            user.authenticationType === AUTHENTICATION_TYPE.email
            && !userEmailVerified
            && setIsUserProfileDialogExpanded
        ) {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: `Verify your email to make a comment. Click "${USER_VERIFY_EMAIL_BUTTON_TEXT}" in the User Dialog`,
                icon: CautionIcon,
                hasFailure: true,
            });
            setIsUserProfileDialogExpanded(true);
            return false;
        }

        const annotationCommentDraftStorageKey = `annotation-comment-draft:${annotation.id}:${user.id}`;
        const restoreCommentDraft = (): void => {
            if (!annotationCommentEditor) return;
            try {
                const draft = sessionStorage.getItem(annotationCommentDraftStorageKey);
                if (!draft) return;
                const parsedDraft = JSON.parse(draft) as { value?: string };
                if (!parsedDraft.value) return;
                const slateValue = JSON.parse(parsedDraft.value) as Descendant[];
                if (!Array.isArray(slateValue) || slateValue.length === 0) return;
                annotationCommentEditor.children = slateValue;
                annotationCommentEditor.onChange();
                const endPoint = Editor.end(annotationCommentEditor, []);
                Transforms.select(annotationCommentEditor, endPoint);
            } catch (error) {
                // Best-effort local recovery only
            }
        };

        try {
            try {
                sessionStorage.setItem(
                    annotationCommentDraftStorageKey,
                    JSON.stringify({
                        value,
                        text,
                        savedAt: Date.now(),
                    }),
                );
            } catch (error) {
                // Best-effort local recovery only
            }
            const optimisticCommentId = `pending-comment-${uuidv4()}`;
            const createdTimestamp = Date.now();
            const cachedComments = annotationCommentsCache.get(annotationCanonicalId);
            const nextLoadedCommentIndex = getHighestCommentIndex(annotationComments.values()) + 1;
            const nextCachedCommentIndex = cachedComments
                ? getHighestCommentIndex(cachedComments.values()) + 1
                : 0;
            const nextEngagementCommentIndex = annotationEngagement?.commentCount ?? 0;
            const optimisticCommentIndex = Math.max(
                nextLoadedCommentIndex,
                nextCachedCommentIndex,
                nextEngagementCommentIndex,
            );
            const optimisticComment: IAnnotationItem = {
                id: optimisticCommentId,
                postId: annotation.postId,
                targetScope: ANNOTATION_TARGET_SCOPE.annotation,
                targetId: annotationCanonicalId,
                chapterId: annotation.chapterId,
                sectionId: annotation.sectionId,
                parentAnnotationId: annotationCanonicalId,
                isComment: true,
                comment: {
                    parentId: annotationCanonicalId,
                    index: optimisticCommentIndex,
                },
                history: [{
                    id: uuidv4(),
                    timestamp: createdTimestamp,
                    value,
                    text,
                }],
                userId: user.id,
                media: [...media],
                quoteHistory: [],
                published: createdTimestamp,
                deleted: {
                    deleted: false,
                    timestamp: null,
                    deletedBy: null,
                },
            };
            setAnnotationComments((prev) => {
                const updated = new Map([
                    ...Array.from(prev),
                ]);
                updated.set(optimisticCommentId, optimisticComment);
                return updated;
            });
            setPendingCommentCreationIds((prev) => {
                const updated = new Set(prev);
                updated.add(optimisticCommentId);
                return updated;
            });
            if (!clientIsOnline()) {
                setQueuedCommentRetries((prev) => {
                    const updated = new Map(prev);
                    updated.set(optimisticCommentId, {
                        postId: annotationPostId,
                        parentId: annotationCanonicalId,
                        commentIndex: optimisticCommentIndex,
                        value,
                        text,
                        mediaIds: [...media],
                        zoomLevel,
                        chapterId: annotation.chapterId,
                        sectionId: annotation.sectionId,
                        draftStorageKey: annotationCommentDraftStorageKey,
                        attempt: 0,
                        nextRetryAt: Date.now() + COMMENT_RETRY_BASE_DELAY_MS,
                        inFlight: false,
                    });
                    return updated;
                });
                return true;
            }
            createAnnotation({
                test: isTestMode(),
                postId: annotationPostId,
                isComment: true,
                value,
                text,
                comment: {
                    parentId: annotationCanonicalId,
                    // We use annotationComments because it reflects the latest thread state from live fetches/listeners.
                    index: optimisticCommentIndex,
                },
                mediaIds: media,
                zoomLevel,
                chapterId: annotation.chapterId,
                sectionId: annotation.sectionId,
                // Reuse optimistic id as idempotency key for backend comment-create retries.
                requestId: optimisticCommentId,
            }).then((annotationComment) => {
                try {
                    sessionStorage.removeItem(annotationCommentDraftStorageKey);
                } catch (error) {
                    // Best-effort local recovery only
                }
                setAnnotationComments((prev) => {
                    const updated = new Map([
                        ...Array.from(prev),
                    ]);
                    updated.delete(optimisticCommentId);
                    updated.set(annotationComment.id, annotationComment);
                    return updated;
                });
                setQueuedCommentRetries((prev) => {
                    const updated = new Map(prev);
                    updated.delete(optimisticCommentId);
                    return updated;
                });
                setPendingCommentCreationIds((prev) => {
                    const updated = new Set(prev);
                    updated.delete(optimisticCommentId);
                    // Protect server id from snapshot lag pruning until seen in server map.
                    updated.add(annotationComment.id);
                    return updated;
                });
                clearTimeoutAnnotationNudge();
                if (isTestMode()) {
                    // eslint-disable-next-line no-console
                    console.log('[optimistic-comment]', 'acked', {
                        optimisticCommentId,
                        commentId: annotationComment.id,
                        annotationId: annotationCanonicalId,
                    });
                }
                const cur = handleSubmitAnnotationCommentLatest.current;
                if (cur.currentSessionId) {
                    // Record user action
                    recordUserAction({
                        test: isTestMode(),
                        type: USER_ACTION_TYPE.createAnnotationComment,
                        sessionId: cur.currentSessionId,
                        payload: {
                            annotationId: annotationComment.id,
                        },
                    });
                }
            }).catch(() => {
                setQueuedCommentRetries((prev) => {
                    const updated = new Map(prev);
                    updated.set(optimisticCommentId, {
                        postId: annotationPostId,
                        parentId: annotationCanonicalId,
                        commentIndex: optimisticCommentIndex,
                        value,
                        text,
                        mediaIds: [...media],
                        zoomLevel,
                        chapterId: annotation.chapterId,
                        sectionId: annotation.sectionId,
                        draftStorageKey: annotationCommentDraftStorageKey,
                        attempt: 0,
                        nextRetryAt: Date.now() + COMMENT_RETRY_BASE_DELAY_MS,
                        inFlight: false,
                    });
                    return updated;
                });
                showCommentCreateErrorSnackbar();
            });
            return true;
        } catch (e) {
            restoreCommentDraft();
            showCommentCreateErrorSnackbar();
            return false;
        }
    };

    const fetchUnreadNotifications = async (async?: boolean): Promise<void> => {
        if (!clientIsOnline()) return;
        if (annotationCanonicalId.startsWith('pending-')) return;
        if (user && isAnnotationAuthor) {
            if (async) {
                const notifications = await getAnnotationUnreadNotifications({
                    test: isTestMode(),
                    annotationId: annotationCanonicalId,
                });
                setUnreadNotifications(notifications);
                if (!fetchedUnreadNotifications) setFetchedUnreadNotifications(true);
            } else {
                getAnnotationUnreadNotifications({
                    test: isTestMode(),
                    annotationId: annotationCanonicalId,
                }).then((notifications) => {
                    setUnreadNotifications(notifications);
                    if (!fetchedUnreadNotifications) setFetchedUnreadNotifications(true);
                }).catch(() => {
                    // Best-effort fetch; avoid noisy offline/internal surfacing during scroll visibility updates.
                });
            }
        }
    };

    const getDictationAudioLatest = useLatest({
        annotation,
        annotationAudioURLs,
        dictationReadyKey,
        lastFetchedDictationReadyKey,
        getDictationAttempts,
        fetchingDictationAudio,
        addToAnnotationAudioURLs,
        setLastFetchedDictationReadyKey,
        setFetchingDictationAudio,
    });
    const getDictationAudio = (): void => {
        const cur = getDictationAudioLatest.current;
        if (annotation.dictationFilePaths) {
            // Dictation audio may have been fetched under the optimistic UI id
            // before the server row exists. Check both ids so the canonical
            // annotation can immediately reuse that cached audio URL after ack.
            const cachedAudioURL = cur.annotationAudioURLs.get(annotationCanonicalId)
                || cur.annotationAudioURLs.get(annotation.id);
            if (cachedAudioURL) {
                if (cur.lastFetchedDictationReadyKey !== cur.dictationReadyKey) {
                    cur.setLastFetchedDictationReadyKey(cur.dictationReadyKey);
                }
                // Once we have a cached dictation URL for this annotation, do
                // not keep polling Storage for the same asset.
                return;
            }
            if (!fetchingDictationAudio) setFetchingDictationAudio(true);
            const playbackSelection = selectAnnotationDictationPlaybackPath({
                dictationFilePaths: annotation.dictationFilePaths,
            });
            if (!playbackSelection.playbackPath) {
                // The annotation fan-out can leave a temporary WAV in
                // Firestore before the final MP3 has been written. Wait for
                // the browser-facing MP3 instead of fetching the source file.
                setFetchingDictationAudio(false);
                if (
                    playDictationAfterFetchRef.current
                    && canUseLocalDictationSpeech
                ) {
                    playDictationAfterFetchRef.current = false;
                    startLocalDictationSpeech();
                    return;
                }
                if (playbackSelection.shouldPollForMp3) {
                    clearTimeoutGetDictationAudio();
                    timeoutGetDictationAudio();
                }
                return;
            }
            const storage = getStorage();
            getDownloadURL(ref(storage, playbackSelection.playbackPath))
                .then((url) => {
                    const thenCur = getDictationAudioLatest.current;
                    clearTimeoutGetDictationAudio();
                    Promise.all([
                        // Cache under the canonical id for the settled row and
                        // also under the current local id while the optimistic
                        // row is still mounted during the handoff.
                        thenCur.addToAnnotationAudioURLs({
                            annotationId: annotationCanonicalId,
                            url,
                        }),
                        ...(thenCur.annotation.id !== annotationCanonicalId
                            ? [thenCur.addToAnnotationAudioURLs({
                                annotationId: thenCur.annotation.id,
                                url,
                            })]
                            : []),
                    ]).then(() => {
                        if (audioNodeRef.current) {
                            audioNodeRef.current.volume = DEFAULT_AUDIO_VOLUME;
                            audioNodeRef.current.src = url;
                            audioNodeRef.current.load();
                        }
                        thenCur.setLastFetchedDictationReadyKey(thenCur.dictationReadyKey);
                        // don't wrap in a conditional that checks if state variable is true
                        // this is because we set it in the same method we call this which causes weird behavior
                        thenCur.setFetchingDictationAudio(false);
                        if (playDictationAfterFetchRef.current) {
                            playDictationAfterFetchRef.current = false;
                            window.setTimeout(() => {
                                startDictationPlayback();
                            }, 0);
                        }
                    });
                })
                .catch((error) => {
                    const catchCur = getDictationAudioLatest.current;
                    const shouldPlayLocalFallback = playDictationAfterFetchRef.current;
                    playDictationAfterFetchRef.current = false;
                    // Always clear fetching state so subsequent attempts are not blocked.
                    catchCur.setFetchingDictationAudio(false);

                    if (
                        shouldPlayLocalFallback
                        && canUseLocalDictationSpeech
                    ) {
                        startLocalDictationSpeech();
                        return;
                    }

                    if (
                        error.code === STORAGE_ERROR_CODE.objectNotFound
                        || error.code === STORAGE_ERROR_CODE.retryLimitExceeded
                        || error.code === STORAGE_ERROR_CODE.unknown
                    ) {
                        clearTimeoutGetDictationAudio();
                        timeoutGetDictationAudio();
                    }
                });
        }
    };

    const markAnnotationCommentNotifsAsReadLatest = useLatest({
        unreadNotifications,
        fetchedUnreadNotifications,
    });
    const markAnnotationCommentNotifsAsRead = (): void => {
        const markUnread = async (): Promise<void> => {
            if (!clientIsOnline()) return;
            const cur = markAnnotationCommentNotifsAsReadLatest.current;
            // mark notifications as read
            if (cur.unreadNotifications) {
                const updateNotificationPromises: Promise<boolean>[] = [];
                cur.unreadNotifications.forEach((notif) => {
                    updateNotificationPromises.push(
                        markNotificationAsRead({
                            test: isTestMode(),
                            notificationId: notif.id,
                        }),
                    );
                });

                Promise.all(updateNotificationPromises).then(() => {
                    setRefreshNotifications(true);
                    setUnreadNotifications([]);
                });
            }
        };
        // get annotation comment notifications
        if (
            user
            && unreadNotifications == null
            && !fetchedUnreadNotifications
        ) {
            fetchUnreadNotifications(true).then(() => {
                const cur = markAnnotationCommentNotifsAsReadLatest.current;
                if (
                    cur.unreadNotifications
                    && cur.unreadNotifications.length > 0
                    && cur.fetchedUnreadNotifications
                ) {
                    markUnread();
                }
            });
        } else if (
            unreadNotifications
            && unreadNotifications.length > 0
            && fetchedUnreadNotifications
        ) {
            markUnread();
        }
    };

    const refreshCommentLatest = useLatest({
        annotationComments,
    });
    const refreshComment = ({ annotationId }: { annotationId: string }): void => {
        if (!clientIsOnline()) return;
        getAnnotationComments({
            test: isTestMode(),
            annotationId: annotationCanonicalId,
        }).then((commentMap) => {
            const cur = refreshCommentLatest.current;
            const updatedAnnotationComments = new Map([
                ...Array.from(cur.annotationComments),
            ]);
            if (commentMap.get(annotationId)) {
                const updatedComment = commentMap.get(annotationId)!;
                updatedAnnotationComments.set(annotationId, updatedComment);
            }
            setAnnotationComments(updatedAnnotationComments);
        }).catch(() => {
            // Best-effort refresh; avoid noisy offline/internal surfacing during scroll visibility updates.
        });
    };

    // ===== Side Effects =====

    useEffect(() => () => {
        if (deleteClip.current) {
            deleteClip.current.pause();
            deleteClip.current.remove();
            deleteClip.current = null;
        }
    }, []);

    useEffect(() => {
        const savedValueSummary = getSavedAnnotationValueSummary();
        logAnnotationEditDebug('annotation-read-edit-state', {
            annotationId: annotation.id,
            isEditing,
            isFocused,
            isComment: !!annotation.comment && annotationIsComment,
            hasAnnotationEditor: !!annotationEditor,
            editorNodeCount: annotationEditor ? annotationEditor.children.length : null,
            editorTextLength: annotationEditor ? Node.string(annotationEditor).length : null,
            historyLength: annotation.history.length,
            ...savedValueSummary,
        });
    }, [
        annotation.id,
        annotation.comment,
        annotation.history,
        annotationEditor,
        annotationIsComment,
        getSavedAnnotationValueSummary,
        isEditing,
        isFocused,
    ]);

    /**
     * Listen for annotation/comment updates
     */
    useEffect(() => {
        setListenForAnnotationUpdates(true);
    }, []);

    /**
     * Tells annotation bucket to remeasure itself once all annotations have rendered
     */
    useEffect(() => {
        if (
            !annotationIsComment
            && !annotation.comment
            && !!containerRef.current
            && index + 1 === annotationCount
            && remeasureBucketHeight != null
            && !remeasureBucketHeight
        ) {
            remeasureParentHeight();
        }
    }, [containerRef.current]);

    /**
     * Remeasures bucket height so annotation stacks
     * adjust
     */
    useEffect(() => {
        if (remeasureAnnotationHeight && containerRef.current) {
            clearTimeoutMeasureAnnotationHeight();
            timeoutMeasureAnnotationHeight();
        }
    }, [
        remeasureAnnotationHeight,
    ]);

    /**
     * Loads the annotation author's data
     */
    useEffect(() => {
        const annotationAuthor = postUserItems.get(annotation.userId);
        if (
            !annotationAuthor
            && auth.currentUser
            && annotationIsVisible
        ) {
            fetchAnnotationAuthor();
        }
    }, [
        annotation.userId,
        postUserItems,
        annotationIsVisible,
    ]);

    /**
     * Listens for new comments in Firestore
     */
    // keep all changing values fresh for the snapshot callback
    const annotationLatest = useLatest({
        listenForAnnotationUpdates,
        subscribedToAnnotationUpdates,
        lastAnnotationPingVersion,
        annotation,
        setSubscribedToAnnotationUpdates,
        setLastAnnotationPingVersion,
        refreshAnnotation,
    });
    const annotationUnsubscribeRef = React.useRef<Unsubscribe | null>(null);
    // (Re)subscribe when the *conditions* change; do manual teardown inside.
    useEffect(() => {
        const cur = annotationLatest.current;

        // If we shouldn't be listening now, tear down and bail.
        if (!cur.listenForAnnotationUpdates && !cur.subscribedToAnnotationUpdates) {
            if (annotationUnsubscribeRef.current) {
                annotationUnsubscribeRef.current();
                annotationUnsubscribeRef.current = null;
            }
            return;
        }

        // If already subscribed, reset before creating a new one.
        if (annotationUnsubscribeRef.current) {
            annotationUnsubscribeRef.current();
            annotationUnsubscribeRef.current = null;
        }

        const db = getFirestore();
        const annotationCollection = isTestMode()
            ? FIRESTORE_COLLECTION.stagingAnnotations
            : FIRESTORE_COLLECTION.annotations;

        const annotationSourceId = (
            cur.annotation as (IAnnotationItem & { canonicalId?: string })
        ).canonicalId || cur.annotation.id;
        const pingRef = doc(db, `${annotationCollection}/${annotationSourceId}/annotation-ping/current`);
        annotationUnsubscribeRef.current = onSnapshot(pingRef, async (pingSnapshot) => {
            const snapshotCur = annotationLatest.current;
            if (!pingSnapshot.exists()) {
                // no annotation updates yet
                return;
            }

            // annotation but no ping item yet (will be made when next update is posted)
            const { version } = pingSnapshot.data() as { version: number };

            // ignore stale/no-op
            if (
                snapshotCur.lastAnnotationPingVersion != null
                && version <= snapshotCur.lastAnnotationPingVersion) {
                return;
            }

            if (!snapshotCur.subscribedToAnnotationUpdates) snapshotCur.setSubscribedToAnnotationUpdates(true);
            if (annotationRefreshTimeoutRef.current != null) {
                window.clearTimeout(annotationRefreshTimeoutRef.current);
            }
            // Debounce burst ping updates to avoid duplicate refresh callables.
            annotationRefreshTimeoutRef.current = window.setTimeout(() => {
                const timeoutCur = annotationLatest.current;
                timeoutCur.refreshAnnotation({
                    annotationId: timeoutCur.annotation.id,
                });
            }, PING_REFRESH_DEBOUNCE_DURATION);
            snapshotCur.setLastAnnotationPingVersion(version);
        });
    }, [
        listenForAnnotationUpdates,
    ]);
    // Unmount-only cleanup
    React.useEffect(() => () => {
        if (annotationUnsubscribeRef.current) {
            annotationUnsubscribeRef.current();
            annotationUnsubscribeRef.current = null;
        }
        if (annotationRefreshTimeoutRef.current != null) {
            window.clearTimeout(annotationRefreshTimeoutRef.current);
            annotationRefreshTimeoutRef.current = null;
        }
    }, []);

    /**
     * Listens for new comments in Firestore
     */
    // keep all changing values fresh for the snapshot callback
    const commentLatest = useLatest({
        listenForAnnotationComments,
        subscribedToCommentUpdates,
        lastCommentThreadPingVersion,
        fetchingAnnotationComments,
        fetchedAnnotationComments,
        annotation,
        getAnnotationComments,
        setAnnotationComments,
        annotationComments,
        commentEngagementItems,
        pendingCommentCreationIds,
        setPendingCommentCreationIds,
        setSubscribedToCommentUpdates,
        setLastCommentThreadPingVersion,
        setFetchedAnnotationComments,
        setFetchingAnnotationComments,
        setAnnotationCommentsRequestFailed,
        setAnnotationCommentsRetryCount,
    });
    const commentUnsubscribeRef = React.useRef<Unsubscribe | null>(null);
    // (Re)subscribe when the *conditions* change; do manual teardown inside.
    useEffect(() => {
        const cur = commentLatest.current;

        // If we shouldn't be listening now, tear down and bail.
        if (!cur.listenForAnnotationComments && !cur.subscribedToCommentUpdates) {
            if (commentUnsubscribeRef.current) {
                commentUnsubscribeRef.current();
                commentUnsubscribeRef.current = null;
            }
            return;
        }

        // If already subscribed, reset before creating a new one.
        if (commentUnsubscribeRef.current) {
            commentUnsubscribeRef.current();
            commentUnsubscribeRef.current = null;
        }

        const db = getFirestore();
        const annotationCollection = isTestMode()
            ? FIRESTORE_COLLECTION.stagingAnnotations
            : FIRESTORE_COLLECTION.annotations;

        const annotationSourceId = (
            cur.annotation as (IAnnotationItem & { canonicalId?: string })
        ).canonicalId || cur.annotation.id;
        const pingRef = doc(db, `${annotationCollection}/${annotationSourceId}/comments-ping/current`);
        commentUnsubscribeRef.current = onSnapshot(pingRef, async (pingSnapshot) => {
            const snapshotCur = commentLatest.current;
            if (!pingSnapshot.exists()) {
                // no comment updates yet
                return;
            }

            // comments but no ping item yet (will be made when next comment is posted)
            const { version } = pingSnapshot.data() as { version: number };

            // ignore stale/no-op
            if (version <= snapshotCur.lastCommentThreadPingVersion) return;

            snapshotCur.setLastCommentThreadPingVersion(version);
            if (commentsRefreshTimeoutRef.current != null) {
                window.clearTimeout(commentsRefreshTimeoutRef.current);
            }
            // Collapse ping bursts into a single comments refresh.
            commentsRefreshTimeoutRef.current = window.setTimeout(() => {
                const timeoutCur = commentLatest.current;
                if (
                    timeoutCur.annotationComments.size === 0
                    && !timeoutCur.fetchedAnnotationComments
                ) {
                    timeoutCur.setFetchingAnnotationComments(true);
                }
                const commentSourceAnnotationId = (
                    timeoutCur.annotation as (IAnnotationItem & { canonicalId?: string })
                ).canonicalId || timeoutCur.annotation.id;
                timeoutCur.getAnnotationComments({
                    test: isTestMode(),
                    annotationId: commentSourceAnnotationId,
                }).then((commentMap) => {
                    const reconciledPendingCommentIds = new Set<string>();
                    const persistedPendingCommentIds = new Set<string>();
                    const serverEquivalenceKeys = new Set<string>();
                    commentMap.forEach((serverComment) => {
                        const key = buildCommentEquivalenceKey(serverComment);
                        if (key) serverEquivalenceKeys.add(key);
                    });

                    timeoutCur.pendingCommentCreationIds.forEach((pendingCommentId: string) => {
                        if (commentMap.has(pendingCommentId)) {
                            persistedPendingCommentIds.add(pendingCommentId);
                            return;
                        }
                        const pendingComment = timeoutCur.annotationComments.get(pendingCommentId);
                        if (pendingComment) {
                            const pendingKey = buildCommentEquivalenceKey(pendingComment);
                            if (
                                pendingKey
                                && serverEquivalenceKeys.has(pendingKey)
                            ) {
                                reconciledPendingCommentIds.add(pendingCommentId);
                            }
                        }
                    });
                    // Merge with latest local state to avoid dropping optimistic comments
                    // due to stale closure snapshots during offline/online transitions.
                    timeoutCur.setAnnotationComments((prev) => {
                        const finalCommentMap = mergeFetchedAnnotationComments(
                            commentMap,
                            prev,
                            timeoutCur.commentEngagementItems,
                        );
                        prev.forEach((localComment, localCommentId) => {
                            if (
                                localCommentId.startsWith('pending-comment-')
                                && !finalCommentMap.has(localCommentId)
                            ) {
                                const localKey = buildCommentEquivalenceKey(localComment);
                                const hasServerEquivalent = (
                                    localKey != null
                                    && serverEquivalenceKeys.has(localKey)
                                );
                                if (!hasServerEquivalent) {
                                    finalCommentMap.set(localCommentId, localComment);
                                }
                            }
                        });
                        return finalCommentMap;
                    });
                    if (reconciledPendingCommentIds.size > 0) {
                        timeoutCur.setPendingCommentCreationIds((prev) => {
                            const updated = new Set(prev);
                            reconciledPendingCommentIds.forEach((pendingCommentId) => updated.delete(pendingCommentId));
                            return updated;
                        });
                    }
                    if (persistedPendingCommentIds.size > 0) {
                        timeoutCur.setPendingCommentCreationIds((prev) => {
                            const updated = new Set(prev);
                            persistedPendingCommentIds.forEach((pendingCommentId) => updated.delete(pendingCommentId));
                            return updated;
                        });
                    }
                    if (!timeoutCur.fetchedAnnotationComments) timeoutCur.setFetchedAnnotationComments(true);
                    timeoutCur.setAnnotationCommentsRequestFailed(false);
                    timeoutCur.setAnnotationCommentsRetryCount(0);
                    timeoutCur.setFetchingAnnotationComments(false);
                    if (!timeoutCur.subscribedToCommentUpdates) timeoutCur.setSubscribedToCommentUpdates(true);
                }).catch(() => {
                    const catchCur = commentLatest.current;
                    catchCur.setAnnotationCommentsRequestFailed(true);
                    catchCur.setAnnotationCommentsRetryCount((prev) => prev + 1);
                    catchCur.setFetchingAnnotationComments(false);
                });
            }, PING_REFRESH_DEBOUNCE_DURATION);
        });
    }, [
        listenForAnnotationComments,
        mergeFetchedAnnotationComments,
    ]);
    useEffect(() => {
        if (annotationComments.size === 0) {
            setCommentEngagementItems(new Map());
            return undefined;
        }

        return subscribeToAnnotationEngagementItems({
            test: isTestMode(),
            annotationIds: Array.from(annotationComments.keys()),
            onNext: setCommentEngagementItems,
        });
    }, [
        annotationComments,
    ]);

    // Fallback initial load: some legacy annotations may not have comments-ping docs yet.
    // In that case, we still need to fetch comments once when focused.
    useEffect(() => {
        if (
            !isFocused
            || !!annotation.comment
            || annotationIsComment
            || fetchingAnnotationComments
            || fetchedAnnotationComments
            || subscribedToCommentUpdates
            || annotationComments.size > 0
        ) {
            return;
        }

        let active = true;
        setFetchingAnnotationComments(true);
        getAnnotationComments({
            test: isTestMode(),
            annotationId: annotationCanonicalId,
        }).then((commentMap) => {
            if (!active) return;
            applyFetchedAnnotationComments(commentMap);
            setFetchedAnnotationComments(true);
            clearAnnotationCommentRecoveryState();
        }).catch(() => {
            if (!active) return;
            handleAnnotationCommentFetchFailure();
        });

        return () => {
            active = false;
        };
    }, [
        isFocused,
        annotation.comment,
        annotationIsComment,
        subscribedToCommentUpdates,
        annotationCanonicalId,
        applyFetchedAnnotationComments,
        clearAnnotationCommentRecoveryState,
        handleAnnotationCommentFetchFailure,
    ]);

    useEffect(() => {
        if (annotationComments.size === 0) {
            const cachedComments = annotationCommentsCache.get(annotationCanonicalId);
            if (cachedComments && cachedComments.size > 0) {
                setAnnotationComments(new Map(cachedComments));
            }
        }
        if (!fetchedAnnotationComments && annotationCommentsFetchedCache.has(annotationCanonicalId)) {
            setFetchedAnnotationComments(true);
            setFetchingAnnotationComments(false);
            setAnnotationCommentsRequestFailed(false);
            setAnnotationCommentsRetryCount(0);
        }
    }, [
        annotationCanonicalId,
    ]);

    useEffect(() => {
        if (fetchedAnnotationComments) {
            annotationCommentsCache.set(annotationCanonicalId, new Map(annotationComments));
        } else if (annotationComments.size > 0) {
            annotationCommentsCache.set(annotationCanonicalId, new Map(annotationComments));
        }
    }, [
        annotationCanonicalId,
        annotationComments,
        fetchedAnnotationComments,
    ]);

    useEffect(() => {
        if (
            !!annotation.comment
            || annotationIsComment
        ) {
            setAnnotationEngagement(null);
            return undefined;
        }

        return subscribeToAnnotationEngagement({
            test: isTestMode(),
            annotationId: annotationCanonicalId,
            onNext: setAnnotationEngagement,
        });
    }, [
        annotation.comment,
        annotationIsComment,
        annotationCanonicalId,
    ]);

    useEffect(() => {
        if (fetchedAnnotationComments) {
            annotationCommentsFetchedCache.add(annotationCanonicalId);
        }
    }, [
        annotationCanonicalId,
        fetchedAnnotationComments,
    ]);

    /**
     * Reset one-shot recovery state whenever the thread leaves focus so the next
     * open gets a fresh chance to refetch comments instead of inheriting stale
     * failure bookkeeping from the previous session.
     */
    useEffect(() => {
        if (!isFocused) {
            missingCommentsRecoveryAttemptedRef.current = false;
            focusedCommentRefreshAttemptedRef.current = false;
            if (annotationCommentsRequestFailed) setAnnotationCommentsRequestFailed(false);
            if (annotationCommentsRetryCount !== 0) setAnnotationCommentsRetryCount(0);
        }
    }, [
        isFocused,
        annotationCanonicalId,
        annotationCommentsRequestFailed,
        annotationCommentsRetryCount,
    ]);

    /**
     * If this annotation was previously marked as "fetched" but currently has no
     * loaded comments, do one focused refetch on reopen. This covers the stale
     * empty-thread case where earlier state would otherwise skip the normal load.
     */
    useEffect(() => {
        if (
            !isFocused
            || !!annotation.comment
            || annotationIsComment
            || fetchingAnnotationComments
            || annotationComments.size > 0
            || !annotationCommentsFetchedCache.has(annotationCanonicalId)
            || focusedCommentRefreshAttemptedRef.current
            || !clientIsOnline()
        ) {
            return;
        }

        focusedCommentRefreshAttemptedRef.current = true;
        setFetchingAnnotationComments(true);
        getAnnotationComments({
            test: isTestMode(),
            annotationId: annotationCanonicalId,
        }).then((commentMap) => {
            applyFetchedAnnotationComments(commentMap);
            setFetchedAnnotationComments(true);
            clearAnnotationCommentRecoveryState();
        }).catch(() => {
            handleAnnotationCommentFetchFailure();
        });
    }, [
        isFocused,
        annotation.comment,
        annotationIsComment,
        fetchingAnnotationComments,
        annotationComments.size,
        annotationCanonicalId,
        applyFetchedAnnotationComments,
        clearAnnotationCommentRecoveryState,
        handleAnnotationCommentFetchFailure,
    ]);

    /**
     * When the thread is focused and our known comment count says more comments
     * should exist than we have locally, make one recovery fetch to fill the gap.
     * This keeps partially loaded threads from remaining permanently incomplete.
     */
    useEffect(() => {
        const currentLoadedCommentCount = getVisibleAnnotationCommentCount(annotationComments.values());
        const cachedComments = annotationCommentsCache.get(annotationCanonicalId);
        const cachedCommentCount = cachedComments
            ? getVisibleAnnotationCommentCount(cachedComments.values())
            : 0;
        const engagementCommentCount = annotationEngagement?.commentCount ?? 0;
        const cachedKnownCommentCount = annotationCommentCountCache.get(annotationCanonicalId) ?? 0;
        const currentExpectedCommentCount = Math.max(
            currentLoadedCommentCount,
            cachedCommentCount,
            engagementCommentCount,
            cachedKnownCommentCount,
        );

        if (
            !isFocused
            || !!annotation.comment
            || annotationIsComment
            || fetchingAnnotationComments
            || !fetchedAnnotationComments
            || currentLoadedCommentCount >= currentExpectedCommentCount
            || currentExpectedCommentCount === 0
            || missingCommentsRecoveryAttemptedRef.current
        ) {
            return;
        }

        missingCommentsRecoveryAttemptedRef.current = true;
        setFetchingAnnotationComments(true);
        getAnnotationComments({
            test: isTestMode(),
            annotationId: annotationCanonicalId,
        }).then((commentMap) => {
            applyFetchedAnnotationComments(commentMap);
            setFetchedAnnotationComments(true);
            clearAnnotationCommentRecoveryState();
        }).catch(() => {
            handleAnnotationCommentFetchFailure();
        });
    }, [
        isFocused,
        annotation.comment,
        annotationIsComment,
        annotationComments,
        annotationEngagement,
        fetchingAnnotationComments,
        fetchedAnnotationComments,
        annotationCanonicalId,
        applyFetchedAnnotationComments,
        clearAnnotationCommentRecoveryState,
        handleAnnotationCommentFetchFailure,
    ]);

    // iPhone/Safari safety net: recover if comment loading stays stuck while focused.
    useEffect(() => {
        if (
            !isFocused
            || !!annotation.comment
            || annotationIsComment
            || !fetchingAnnotationComments
            || subscribedToCommentUpdates
            || annotationComments.size > 0
        ) {
            return undefined;
        }

        const timeout = setTimeout(() => {
            getAnnotationComments({
                test: isTestMode(),
                annotationId: annotationCanonicalId,
            }).then((commentMap) => {
                applyFetchedAnnotationComments(commentMap);
                setFetchedAnnotationComments(true);
                clearAnnotationCommentRecoveryState();
            }).catch(() => {
                handleAnnotationCommentFetchFailure();
            });
        }, 2500);

        return () => {
            clearTimeout(timeout);
        };
    }, [
        isFocused,
        annotation.comment,
        annotationIsComment,
        fetchingAnnotationComments,
        subscribedToCommentUpdates,
        annotationComments.size,
        annotationCanonicalId,
        applyFetchedAnnotationComments,
        clearAnnotationCommentRecoveryState,
        handleAnnotationCommentFetchFailure,
    ]);

    useEffect(() => {
        if (
            !isFocused
            || !!annotation.comment
            || annotationIsComment
            || fetchingAnnotationComments
            || !annotationCommentsRequestFailed
            || annotationComments.size > 0
            || annotationCommentsRetryCount >= COMMENT_FETCH_MAX_RETRY_COUNT
            || !clientIsOnline()
        ) {
            return undefined;
        }

        const retryDelay = COMMENT_FETCH_RETRY_BASE_DELAY_MS * annotationCommentsRetryCount;
        const timeout = setTimeout(() => {
            setFetchingAnnotationComments(true);
            getAnnotationComments({
                test: isTestMode(),
                annotationId: annotationCanonicalId,
            }).then((commentMap) => {
                applyFetchedAnnotationComments(commentMap);
                setFetchedAnnotationComments(true);
                clearAnnotationCommentRecoveryState();
            }).catch(() => {
                handleAnnotationCommentFetchFailure();
            });
        }, retryDelay);

        return () => {
            clearTimeout(timeout);
        };
    }, [
        isFocused,
        annotation.comment,
        annotationIsComment,
        fetchingAnnotationComments,
        annotationCommentsRequestFailed,
        annotationComments.size,
        annotationCommentsRetryCount,
        annotationCanonicalId,
        applyFetchedAnnotationComments,
        clearAnnotationCommentRecoveryState,
        handleAnnotationCommentFetchFailure,
    ]);

    useEffect(() => {
        if (queuedCommentRetries.size === 0) return;
        const interval = setInterval(() => {
            const cur = queuedCommentRetriesLatest.current;
            const now = Date.now();
            if (!clientIsOnline()) return;

            cur.queuedCommentRetries.forEach((retryItem, optimisticCommentId) => {
                if (retryItem.inFlight || retryItem.nextRetryAt > now) return;

                setQueuedCommentRetries((prev) => {
                    const updated = new Map(prev);
                    const target = updated.get(optimisticCommentId);
                    if (target) {
                        updated.set(optimisticCommentId, {
                            ...target,
                            inFlight: true,
                        });
                    }
                    return updated;
                });

                createAnnotation({
                    test: isTestMode(),
                    postId: retryItem.postId,
                    isComment: true,
                    value: retryItem.value,
                    text: retryItem.text,
                    comment: {
                        parentId: retryItem.parentId,
                        index: retryItem.commentIndex,
                    },
                    mediaIds: retryItem.mediaIds,
                    zoomLevel: retryItem.zoomLevel,
                    chapterId: retryItem.chapterId,
                    sectionId: retryItem.sectionId,
                    requestId: optimisticCommentId,
                }).then((annotationComment) => {
                    try {
                        sessionStorage.removeItem(retryItem.draftStorageKey);
                    } catch (error) {
                        // Best-effort local recovery only
                    }
                    clearTimeoutAnnotationNudge();
                    setAnnotationComments((prev) => {
                        const updated = new Map([
                            ...Array.from(prev),
                        ]);
                        updated.delete(optimisticCommentId);
                        updated.set(annotationComment.id, annotationComment);
                        return updated;
                    });
                    setPendingCommentCreationIds((prev) => {
                        const updated = new Set(prev);
                        updated.delete(optimisticCommentId);
                        // Protect server id from snapshot lag pruning until seen in server map.
                        updated.add(annotationComment.id);
                        return updated;
                    });
                    setQueuedCommentRetries((prev) => {
                        const updated = new Map(prev);
                        updated.delete(optimisticCommentId);
                        return updated;
                    });
                    if (isTestMode()) {
                        // eslint-disable-next-line no-console
                        console.log('[optimistic-comment]', 'acked', {
                            optimisticCommentId,
                            commentId: annotationComment.id,
                            annotationId: retryItem.parentId,
                            recoveredFromRetry: true,
                        });
                    }

                    const thenCur = queuedCommentRetriesLatest.current;
                    if (thenCur.currentSessionId) {
                        recordUserAction({
                            test: isTestMode(),
                            type: USER_ACTION_TYPE.createAnnotationComment,
                            sessionId: thenCur.currentSessionId,
                            payload: {
                                annotationId: annotationComment.id,
                            },
                        });
                    }
                }).catch(() => {
                    setQueuedCommentRetries((prev) => {
                        const updated = new Map(prev);
                        const target = updated.get(optimisticCommentId);
                        if (!target) return updated;
                        const nextAttempt = target.attempt + 1;
                        const nextDelayMs = Math.min(
                            COMMENT_RETRY_BASE_DELAY_MS * (2 ** nextAttempt),
                            COMMENT_RETRY_MAX_DELAY_MS,
                        );
                        updated.set(optimisticCommentId, {
                            ...target,
                            attempt: nextAttempt,
                            nextRetryAt: Date.now() + nextDelayMs,
                            inFlight: false,
                        });
                        return updated;
                    });
                });
            });
        }, 1000);

        return () => {
            clearInterval(interval);
        };
    }, [
        queuedCommentRetries.size,
    ]);
    // Unmount-only cleanup
    React.useEffect(() => () => {
        stopLocalDictationSpeech();
        if (commentUnsubscribeRef.current) {
            commentUnsubscribeRef.current();
            commentUnsubscribeRef.current = null;
        }
        if (ignoreNextContainerClickTimeoutRef.current != null) {
            window.clearTimeout(ignoreNextContainerClickTimeoutRef.current);
            ignoreNextContainerClickTimeoutRef.current = null;
        }
        if (commentsRefreshTimeoutRef.current != null) {
            window.clearTimeout(commentsRefreshTimeoutRef.current);
            commentsRefreshTimeoutRef.current = null;
        }
    }, []);

    /**
     * Focuses the annotation when the annotation id is in the url
     */
    useEffect(() => {
        if (!enableRouteFocusSync) {
            if (isFocused) setIsFocused(false);
            recordedFocusViewRef.current = false;
            return;
        }

        if (
            !!annotationIsFocused
            && params.annotationId
            && params.annotationId === annotation.id
            && !isFocused
            && showAnnotationDetail
        ) {
            setIsFocused(true);
            // useful for when annotation detail banner on smaller viewports
            showAnnotationDetail(annotation);
            // mark any annotation comments as read
            markAnnotationCommentNotifsAsRead();
            // listen for annotation/comment updates
            if (!listenForAnnotationUpdates) {
                setListenForAnnotationUpdates(true);
            }
            // fetch comment annotations
            if (
                !annotation.comment
                && !annotationIsComment
            ) {
                if (!listenForAnnotationComments) {
                    setListenForAnnotationComments(true);
                }
            }
        }

        if (
            !!annotationIsFocused
            && params.annotationId
            && params.annotationId === annotation.id
            && trackViewOnFocus
            && currentSessionId
            && annotationHasCanonicalBackendId
            && !recordedFocusViewRef.current
        ) {
            recordedFocusViewRef.current = true;
            const actionId = uuidv4();
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.viewAnnotation,
                actionId,
                sessionId: currentSessionId,
                payload: {
                    annotationId: annotation.id,
                },
            });
            updateAnnotation({
                test: isTestMode(),
                annotationId: annotation.id,
                viewsToAdd: [actionId],
            }).catch(() => {
                // Best-effort telemetry write; avoid surfacing transient contention to readers.
            });
        } else if (
            (
                !annotationIsFocused
                || (
                    !!annotationIsFocused
                    && params.annotationId
                    && params.annotationId !== annotation.id
                )
            )
            && isFocused
            && hideAnnotationDetail
        ) {
            setIsFocused(false);
            recordedFocusViewRef.current = false;
            if (viewportDimensions.width >= MEDIA_QUERY_SIZE.medium.min) {
                hideAnnotationDetail(true);
            }
        }
    }, [
        params,
        annotationIsFocused,
        isFocused,
        showAnnotationDetail,
        hideAnnotationDetail,
        listenForAnnotationUpdates,
        listenForAnnotationComments,
        annotation.id,
        annotation.comment,
        annotationIsComment,
        enableRouteFocusSync,
        currentSessionId,
        trackViewOnFocus,
        viewportDimensions.width,
    ]);

    // Get dictation audio
    useEffect(() => {
        if (
            audioNodeRef.current
            && hasAnnotationDictationPaths
            && dictationReadyKey != null
            && lastFetchedDictationReadyKey !== dictationReadyKey
            && auth.currentUser
            && (
                annotationIsVisible
                && !hide
            )
            && !fetchingDictationAudio
            && !isUpdating
            && !audioURL
        ) {
            getDictationAudio();
        }
    }, [
        audioNodeRef.current,
        hasAnnotationDictationPaths,
        dictationReadyKey,
        lastFetchedDictationReadyKey,
        annotationIsVisible,
        hide,
    ]);

    // Update elapsed time
    // Stop audio when reached the end
    useInterval(() => {
        if (localDictationUtteranceRef.current != null) {
            return;
        }

        const newElapsed = computeTimeElapsed();
        setTimeElapsed(newElapsed);

        const roundedTimeElapsed = Math.round(timeElapsed * 1000) / 1000;
        const roundedAudioLength = Math.round(audioLength * 1000) / 1000;
        // Stop Playback when reach end of recording
        if (
            audioNodeRef.current
            && dictationPlaying
            && localDictationUtteranceRef.current == null
            && !Number.isNaN(audioLength)
            && roundedTimeElapsed >= roundedAudioLength
        ) {
            // Update Recording timestamp
            setDictationPlaying(false);
            audioNodeRef.current.pause();
            audioNodeRef.current.currentTime = 0;
            setTimeElapsed(0);
            setStartTime(null);
            setSavedTime(null);
            recordFinishDictationAction();
        }
        // The alternative occurs when the metadata is not fetched.
        // This results in immediately ending the song.
        // This is an edge case that happens a handful of times.
    }, dictationPlaying ? MILLISECONDS_IN_A_SECOND / 10 : null);

    // Update audio lenth when metadata updated
    useEventListener(
        'loadedmetadata',
        useCallback((): void => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const { duration } = audioNodeRef.current!;
            // Chromium bug condition
            if (audioNodeRef.current && duration === Infinity) {
                // ...set audioNodeRef.current.currentTime to a very large number
                // ...listen to "timeupdate" event
                // ...on "timeupdate", retrieve now-updated duration
                audioNodeRef.current.currentTime = 10000000 * Math.random();
            } else if (!Number.isNaN(duration)) {
                setAudioLength(duration * MILLISECONDS_IN_A_SECOND);
            }
        }, [audioNodeRef.current]),
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        audioNodeRef.current!,
    );

    /**
     * Fetches user items of all comment authors
     */
    useEffect(() => {
        if (
            !annotation
            || !isFocused
            || annotationComments.size === 0
            || !clientIsOnline()
        ) {
            return;
        }

        const commentAuthorIds = Array.from(new Set(
            Array.from(annotationComments.values()).map((comment) => comment.userId),
        ));
        const missingAuthorIds = commentAuthorIds.filter((id) => !postUserItems.has(id));
        if (missingAuthorIds.length === 0) return;

        // Single-shot author hydration for focused thread.
        fetchAnnotationCommentAuthors();
    }, [
        annotation,
        isFocused,
        annotationComments,
        postUserItems,
    ]);

    const {
        start: timeoutGetDictationAudio,
        clear: clearTimeoutGetDictationAudio,
    } = useTimeout(() => {
        getDictationAudio();
        setGetDictationAttempts((prev) => prev + 1);
    }, GET_DICTATION_TIMEOUT_DURATION);

    const {
        start: timeoutMeasureAnnotationHeight,
        clear: clearTimeoutMeasureAnnotationHeight,
    } = useTimeout(() => {
        if (remeasureAnnotationHeight && containerRef.current) {
            const { height } = containerRef.current.getBoundingClientRect();
            setAnnotationHeight(height);
            setRemeasureAnnotationHeight(false);
        }
    }, MEASURE_ANNOTATION_HEIGHT_TIMEOUT_DURATION);

    // ===== Memoization =====

    const isAnnotationAuthor = useMemo(() => !!user && annotation.userId === user.id, [
        user?.id,
        annotation.userId,
    ]);

    const isPostAuthor = useMemo(() => !!post?.authors && post.authors.includes(annotation.userId), [
        post?.authors,
        annotation.userId,
    ]);

    const hasPostAuthorAvatarImage = useMemo(() => (!!postUserAvatarURLs.get(postAuthorIds[0]) || (
        postAuthorIds.length > 0
        && !!postUserItems.get(postAuthorIds[0])
        && !!postUserItems.get(postAuthorIds[0])?.photoURL
    )), [
        postUserAvatarURLs,
        postAuthorIds,
        postUserItems,
    ]);

    const postAuthorAvatarImageURL = useMemo(() => {
        if (postUserAvatarURLs.get(postAuthorIds[0])) {
            return postUserAvatarURLs.get(postAuthorIds[0])!;
        }

        if (
            postAuthorIds.length > 0
            && postUserItems.get(postAuthorIds[0]) != null
            && postUserItems.get(postAuthorIds[0])?.photoURL != null
        ) {
            return postUserItems.get(postAuthorIds[0])!.photoURL!;
        }

        return null;
    }, [
        postUserAvatarURLs,
        postAuthorIds,
        postUserItems,
    ]);

    const hasAnnotationAuthorAvatarImage = useMemo(() => (
        !!postUserAvatarURLs.get(annotation.userId)
        || !!postUserItems.get(annotation.userId)?.photoURL
    ), [
        annotation,
        postUserAvatarURLs,
        postUserItems,
    ]);

    const annotationAuthorAvatarImageURL = useMemo(() => {
        if (postUserAvatarURLs.get(annotation.userId) != null) {
            return postUserAvatarURLs.get(annotation.userId)!;
        }
        if (postUserItems.get(annotation.userId)?.photoURL != null) {
            return postUserItems.get(annotation.userId)!.photoURL!;
        }

        return null;
    }, [
        annotation,
        postUserAvatarURLs,
        postUserItems,
    ]);

    const annotationAuthor = useMemo(() => postUserItems.get(annotation.userId) || null, [
        annotation,
        postUserItems,
    ]);

    // Prefer the canonical cache entry so the settled annotation can keep the
    // already-fetched dictation audio after the optimistic handoff.
    const audioURL = useMemo(() => (
        annotationAudioURLs.get(annotationCanonicalId)
        || annotationAudioURLs.get(annotation.id)
        || null
    ), [
        annotation,
        annotationAudioURLs,
        fetchingDictationAudio,
    ]);

    const DictationIcon = useMemo(() => (dictationPlaying ? PauseIcon : PlayIcon), [dictationPlaying]);

    const sortedComments = useMemo(() => Array.from(annotationComments.values())
        .sort((a, b) => a.comment!.index - b.comment!.index), [
        annotationComments,
    ]);
    const visibleLoadedCommentCount = useMemo(() => (
        getVisibleAnnotationCommentCount(sortedComments)
    ), [
        sortedComments,
    ]);
    const expectedCommentCount = useMemo(() => {
        const cachedComments = annotationCommentsCache.get(annotationCanonicalId);
        const cachedCommentCount = cachedComments
            ? getVisibleAnnotationCommentCount(cachedComments.values())
            : 0;
        const engagementCommentCount = annotationEngagement?.commentCount ?? 0;
        const cachedKnownCommentCount = annotationCommentCountCache.get(annotationCanonicalId) ?? 0;

        return Math.max(
            visibleLoadedCommentCount,
            cachedCommentCount,
            engagementCommentCount,
            cachedKnownCommentCount,
        );
    }, [
        annotationCanonicalId,
        annotationEngagement,
        visibleLoadedCommentCount,
    ]);
    const displayedSortedComments = useMemo(() => (
        sortedComments.slice(0, renderedCommentCount)
    ), [
        sortedComments,
        renderedCommentCount,
    ]);

    /**
     * Track the highest trustworthy comment count we have seen for this annotation
     * across live data, cached comments, and engagement snapshots so the collapsed
     * indicator does not disappear during transient fetch failures.
     */
    useEffect(() => {
        const fetchedCommentCount = getVisibleAnnotationCommentCount(sortedComments);
        const cachedComments = annotationCommentsCache.get(annotationCanonicalId);
        const cachedCommentCount = cachedComments
            ? getVisibleAnnotationCommentCount(cachedComments.values())
            : 0;
        const engagementCommentCount = annotationEngagement?.commentCount ?? 0;
        const nextKnownCommentCount = Math.max(
            fetchedCommentCount,
            cachedCommentCount,
            engagementCommentCount,
        );

        if (nextKnownCommentCount > 0) {
            annotationCommentCountCache.set(annotationCanonicalId, nextKnownCommentCount);
            return;
        }

        if (
            fetchedAnnotationComments
            && !annotationCommentsRequestFailed
        ) {
            annotationCommentCountCache.set(annotationCanonicalId, 0);
        }
    }, [
        annotationCanonicalId,
        annotationEngagement,
        annotationCommentsRequestFailed,
        fetchedAnnotationComments,
        sortedComments,
    ]);

    useEffect(() => {
        if (
            !isFocused
            || !!annotation.comment
            || annotationIsComment
        ) {
            if (renderedCommentCount !== 0) setRenderedCommentCount(0);
            return;
        }

        setRenderedCommentCount((prev) => {
            if (sortedComments.length === 0) return 0;
            if (prev > 0) return Math.min(prev, sortedComments.length);
            return Math.min(COMMENTS_INITIAL_RENDER_COUNT, sortedComments.length);
        });
    }, [
        isFocused,
        annotation.comment,
        annotationIsComment,
        annotationCanonicalId,
        sortedComments.length,
    ]);

    useEffect(() => {
        if (
            !isFocused
            || !!annotation.comment
            || annotationIsComment
            || renderedCommentCount >= sortedComments.length
        ) {
            return;
        }

        const timeout = window.setTimeout(() => {
            setRenderedCommentCount((prev) => (
                Math.min(
                    sortedComments.length,
                    Math.max(prev, COMMENTS_INITIAL_RENDER_COUNT) + COMMENTS_RENDER_STEP_COUNT,
                )
            ));
        }, 16);

        return () => {
            window.clearTimeout(timeout);
        };
    }, [
        isFocused,
        annotation.comment,
        annotationIsComment,
        renderedCommentCount,
        sortedComments.length,
    ]);

    const commentCount = useMemo(() => {
        if (
            !annotation.comment
            && !annotationIsComment
        ) {
            if (
                fetchedAnnotationComments
                && !annotationCommentsRequestFailed
            ) {
                return Math.max(
                    visibleLoadedCommentCount,
                    annotationEngagement?.commentCount ?? 0,
                );
            }

            return expectedCommentCount;
        }
        return 0;
    }, [
        annotation,
        annotationEngagement,
        annotationCommentsRequestFailed,
        expectedCommentCount,
        fetchedAnnotationComments,
        visibleLoadedCommentCount,
    ]);

    const applyOnMouseEnter = useMemo(() => (
        !annotation.comment
        && !annotationIsComment
        && (
            viewportDimensions.width >= MEDIA_QUERY_SIZE.medium.min
            || !isFocused
        )
        && (
            !annotationStackActive
            || (
                annotationStackActive
                && !annotationStackExpanded
                && index === 0
            ) || (
                annotationStackActive
                && annotationStackExpanded
            )
        )
    ), [
        annotation.comment,
        annotationIsComment,
        viewportDimensions.width,
        isFocused,
        annotationStackActive,
        annotationStackExpanded,
        index,
    ]);

    const isTouchDevice = useMemo(() => (
        typeof document !== 'undefined' && detectTouchDevice(document)
    ), []);

    const disableOnClick = useMemo(() => (
        isFocused
        || isEditing
        || (
            !!annotation.comment
            && annotationIsComment
        )
        || optionsMenuIsVisible
        || (
            annotationStackActive
            && !annotationStackExpanded
            && index > 0
        )
    ), [
        isFocused,
        isEditing,
        annotation.comment,
        annotationIsComment,
        optionsMenuIsVisible,
        annotationStackActive,
        annotationStackExpanded,
        index,
    ]);

    const containerPressHandlers = useMemo(() => {
        if (disableOnClick) {
            return {};
        }
        if (isTouchDevice) {
            return {
                onTouchStart,
                onTouchEnd,
            };
        }
        return {
            onClick,
        };
    }, [
        disableOnClick,
        isTouchDevice,
        onTouchStart,
        onTouchEnd,
        onClick,
    ]);

    /**
     * Computes the height of all prior annotations when new annotations
     */
    const priorAnnotationsHeight = useMemo(() => {
        if (containerRef.current) {
            let priorAccumulatedHeights = 0;
            const siblings = containerRef.current.parentElement!.children;
            for (let i = 0; i < siblings.length; i += 1) {
                const sibling = siblings[i];
                const siblingId = sibling.id;
                if (siblingId === annotation.id) break;
                priorAccumulatedHeights += siblings[i].clientHeight;
            }
            return priorAccumulatedHeights;
        }

        return 0;
    }, [
        bucketHeight,
        annotationCount,
        hide,
        params.annotationId,
    ]);

    // ===== Renderers =====

    const renderModal = (): React.ReactElement => (
        <Modal
            hasContainer
            customCursorActive={customCursorActive}
            hasSound={hasSound}
            isOpen={modal.visible}
            closeModal={modal.rejectCallback}
        >
            <GenericModal
                customCursorActive={customCursorActive}
                message={modal.message}
                callback={modal.proceedCallback}
                hideCancel={!modal.rejectCallback}
                onCursorEnter={onCursorEnter}
                onCursorLeave={onCursorLeave}
            />
        </Modal>
    );

    /**
     * Renderer for the options menu buttons
     */
    const optionsMenuButtonsLatest = useLatest({
        optionsMenuIsVisible,
    });
    const OptionsMenuButtons = useMemo(() => {
        const activeMenuButtons: {
            text: string;
            action: () => void;
        }[] = [];
        const runMenuAction = (action: () => void): (() => void) => () => {
            if (optionsMenuButtonsLatest.current.optionsMenuIsVisible) {
                setOptionsMenuIsVisible(false);
            }
            action();
        };
        const menuButtons = [
            {
                text: isFocused ? 'Close' : 'Open',
                action: runMenuAction(isFocused ? unfocusAnnotation : focusAnnotation),
            },
            {
                text: 'Edit',
                action: runMenuAction(onEditAnnotation),
            },
            {
                text: 'Get Link',
                action: runMenuAction(onGetLink),
            },
            {
                text: 'Delete',
                action: () => {
                    setModal({
                        visible: true,
                        message: DELETE_ANNOTATION_MESSAGE({
                            isComment: !!annotation.comment && annotationIsComment,
                            commentCount,
                        }),
                        proceedCallback: async () => {
                            const cur = optionsMenuButtonsLatest.current;
                            setModal({
                                visible: false,
                                // we keep content, so it doesn't flatten height all of the sudden
                                message: modal.message,
                            });
                            if (cur.optionsMenuIsVisible) setOptionsMenuIsVisible(false);
                            onDeleteAnnotation();
                        },
                        rejectCallback: () => {
                            const cur = optionsMenuButtonsLatest.current;
                            if (cur.optionsMenuIsVisible) {
                                setOptionsMenuIsVisible(false);
                            }
                            setModal({
                                visible: false,
                                // we keep content, so it doesn't flatten height all of the sudden
                                message: modal.message,
                            });
                        },
                    });
                },
            },
        ];

        // Select Annotation
        if (!annotation.comment && !annotationIsComment) activeMenuButtons.push(menuButtons[0]);

        // Edit Annotation
        if (isAnnotationAuthor && !interactionDisabled) activeMenuButtons.push(menuButtons[1]);

        // Get Link of Annotation
        if (!annotation.comment && !annotationIsComment) activeMenuButtons.push(menuButtons[2]);

        // Delete Annotation
        if (isAnnotationAuthor && !interactionDisabled) activeMenuButtons.push(menuButtons[3]);

        return activeMenuButtons.map((button) => (
            <Button
                key={button.text}
                center
                className={HOVER_TARGET_CLASSNAME}
                customCursorActive={customCursorActive}
                type={BUTTON_TYPE.secret}
                height={OPTION_BUTTON_HEIGHT}
                width={OPTION_BUTTON_WIDTH}
                text={button.text}
                {...getTouchMouseEventHandlers({
                    onMouseEnter: onButtonMouseEnter,
                    onMouseLeave: onButtonMouseLeave,
                })}
                {...(isTouchDevice ? {
                    onTouchStart: button.action,
                } : {
                    onMouseDown: button.action,
                })}
            />
        ));
    }, [
        isFocused,
        viewportDimensions.width,
        isAnnotationAuthor,
        annotationComments,
        interactionDisabled,
        post,
        customCursorActive,
        isTouchDevice,
    ]);

    const AnnotationComponent = useMemo(() => (
        <Container
            ref={containerRef}
            id={annotation.id}
            className={HOVER_TARGET_CLASSNAME}
            customCursorActive={customCursorActive}
            index={index}
            zIndex={zIndex}
            isFocused={isFocused}
            isComment={annotationIsComment}
            hide={hide}
            horizontalMargin={horizontalMargin}
            annotationStackActive={annotationStackActive}
            annotationStackExpanded={annotationStackExpanded}
            stackOffsetIncrementLength={ANNOTATION_STACK_OFFSET_INCREMENT_LENGTH}
            priorAnnotationsHeight={priorAnnotationsHeight}
            transitionDuration={ANNOTATION_TRANSITION_DURATION}
            {...(applyOnMouseEnter ? {
                onMouseEnter,
                onMouseLeave,
            } : {}
            )}
            {...containerPressHandlers}
        >
            {renderModal()}
            {!isEditing
            && showAnnotationOptionsMenu
            && (
                (
                    !annotation.comment
                    && !annotationIsComment
                ) || (
                    !!annotation.comment
                    && annotationIsComment
                    && isAnnotationAuthor
                )
            )
            && (
                <OptionsMenuContainer
                    isComment={annotationIsComment}
                    isAuthor={isAnnotationAuthor}
                    length={OPTION_BUTTON_HEIGHT}
                    xOffset={MORE_BUTTON_X_OFFSET}
                >
                    <OptionsMenu
                        center
                        className={HOVER_TARGET_CLASSNAME}
                        customCursorActive={customCursorActive}
                        revealAbove={false}
                        buttonHeight={ANNOTATION_MORE_BUTTON_LENGTH}
                        childItemHeight={OPTION_BUTTON_HEIGHT}
                        childItemWidth={OPTION_BUTTON_WIDTH}
                        parentRef={containerRef.current
                            ? containerRef.current.parentElement
                            : null}
                        tooltipText="More"
                        tooltipSideType={!!annotation.comment && annotationIsComment && isAnnotationAuthor
                            ? TOOLTIP_TYPE.top
                            : TOOLTIP_TYPE.left}
                        onToggle={onOptionsMenuToggle}
                        onHoverEnter={onButtonMouseEnter}
                        onHoverLeave={onButtonMouseLeave}
                    >
                        {OptionsMenuButtons}
                    </OptionsMenu>
                </OptionsMenuContainer>
            )}
            {isFocused
            && !annotation.comment
            && !annotationIsComment
            && viewportDimensions.width >= MEDIA_QUERY_SIZE.medium.min
            && (
                <CloseButtonContainer
                    buttonHeight={ANNOTATION_FOCUSED_CLOSE_BUTTON_HEIGHT}
                >
                    <Button
                        boxShadow
                        className={HOVER_TARGET_CLASSNAME}
                        customCursorActive={customCursorActive}
                        type={BUTTON_TYPE.solid}
                        height={ANNOTATION_FOCUSED_CLOSE_BUTTON_HEIGHT}
                        background={themeObj.verascopeColor.purple400}
                        text="Close"
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: onButtonMouseEnter,
                            onMouseLeave: onButtonMouseLeave,
                        })}
                        {...getPressEventHandlers({ onMouseDown: unfocusAnnotation })}
                    />
                </CloseButtonContainer>
            )}
            <ScrollContainer
                isEditing={isEditing}
            >
                {shouldRenderAnnotationQuote && (
                    <QuoteContainer
                        data-annotation-interactive={annotationQuoteIsInteractive ? 'true' : undefined}
                        interactive={annotationQuoteIsInteractive}
                        marginTop={QUOTE_CONTAINER_MARGIN_TOP}
                        transitionDuration={ANNOTATION_TRANSITION_DURATION}
                        {...(annotationQuoteIsInteractive
                            ? {
                                ...getTouchMouseEventHandlers({
                                    onMouseEnter: onButtonMouseEnter,
                                    onMouseLeave: onButtonMouseLeave,
                                }),
                                ...getPressEventHandlers({ onMouseDown: scrollQuoteIntoView }),
                            }
                            : {}
                        )}
                    >
                        <QuoteText
                            transitionDuration={ANNOTATION_TRANSITION_DURATION}
                        >
                            {latestAnnotationQuoteText}
                        </QuoteText>
                        <PostAuthorSignature>
                            <AvatarContainer
                                hasAvatarImage={hasPostAuthorAvatarImage}
                                length={AVATAR_LENGTH}
                            >
                                {hasPostAuthorAvatarImage && postAuthorAvatarImageURL
                                    ? (
                                        <UserAvatar
                                            url={postAuthorAvatarImageURL} // Has to be present if avatarURL is false
                                            length={USER_PROFILE_AVATAR_LENGTH_MULTIPLIER * BODY_FONT_SIZE}
                                        />
                                    ) : (
                                        <ReactSVG
                                            src={SmileyIcon}
                                        />
                                    )}
                            </AvatarContainer>
                            <PostAuthorDetails>
                                {postAuthorIds.length > 0
                                && (
                                    <PostAuthorName>
                                        {postAuthorIds.reduce((acc, authorId, authorIndex) => {
                                            const author = postUserItems.get(authorId);
                                            if (
                                                author != null
                                                && authorIndex === 0
                                                && postAuthorIds.length === 1
                                            ) {
                                                return `${author.firstName} ${author.lastName}`;
                                            }

                                            if (
                                                author != null
                                                && authorIndex === 0
                                                && postAuthorIds.length > 1) {
                                                return `${author.firstName}`;
                                            }

                                            if (
                                                author != null
                                                && authorIndex !== 0
                                                && postAuthorIds.length > 1
                                            ) {
                                                return `${acc}, ${author.firstName}`;
                                            }

                                            if (author != null) {
                                                return `${acc}, ${author.firstName} ${author.lastName}`;
                                            }

                                            return acc;
                                        }, '')}
                                    </PostAuthorName>
                                )}
                                {post
                                && (
                                    <PostAuthorPostName>
                                        {post?.title}
                                    </PostAuthorPostName>
                                )}
                            </PostAuthorDetails>
                        </PostAuthorSignature>
                    </QuoteContainer>
                )}
                <AnnotationBody
                    isFocused={isFocused}
                    isComment={!!annotation.comment && annotationIsComment}
                    isAuthor={isAnnotationAuthor}
                    isEditing={isEditing}
                    {...(!isFocused
                        && !annotation.comment
                        && !annotationIsComment
                        && annotationStackActive
                        && !annotationStackExpanded
                        && annotationHeight != null
                        && annotationHeight > MAX_ANNOTATION_HEIGHT ? {
                            maxHeight: MAX_ANNOTATION_HEIGHT,
                        } : {})}
                    annotationStackActive={annotationStackActive}
                    annotationStackExpanded={annotationStackExpanded}
                >
                    <UserSignature
                        isFocused={isFocused}
                        isComment={!!annotation.comment && annotationIsComment}
                        isAuthor={isAnnotationAuthor}
                        hideUserSignature={hideUserSignature}
                        padding={USER_SIGNATURE_PADDING}
                        marginLength={MORE_BUTTON_X_OFFSET + ANNOTATION_MORE_BUTTON_LENGTH}
                        transitionDuration={0}
                    >
                        {!hideUserSignature && (
                            <AvatarContainer
                                hasAvatarImage={hasAnnotationAuthorAvatarImage}
                                length={AVATAR_LENGTH}
                                withRing={isPostAuthor
                                    && viewportDimensions.width < MEDIA_QUERY_SIZE.medium.min}
                            >
                                {hasAnnotationAuthorAvatarImage && annotationAuthorAvatarImageURL
                                    ? (
                                        <UserAvatar
                                            url={annotationAuthorAvatarImageURL} // Has to be present if avatarURL is false
                                            length={USER_PROFILE_AVATAR_LENGTH_MULTIPLIER * BODY_FONT_SIZE}
                                        />
                                    ) : (
                                        <ReactSVG
                                            src={SmileyIcon}
                                        />
                                    )}
                            </AvatarContainer>
                        )}
                        {(
                            viewportDimensions.width >= MEDIA_QUERY_SIZE.medium.min
                            || isFocused
                            || (
                                !!annotation.comment
                                && annotationIsComment
                            )
                        ) && (
                            <AnnotationDetails
                                isComment={!!annotation.comment && annotationIsComment}
                                isAuthor={isAnnotationAuthor}
                            >
                                {annotationAuthor && !hideUserSignature && (
                                    <AnnotationAuthorName
                                        fontMultiplier={ANNOTATION_AUTHOR_FONT_MULTIPLIER}
                                    >
                                        {`${annotationAuthor?.firstName} ${annotationAuthor?.lastName}`}
                                        {isPostAuthor && !isAnnotationAuthor && (
                                            <AuthorTag>
                                                Author
                                            </AuthorTag>
                                        )}
                                        {!!annotation.comment && annotationIsComment && isAnnotationAuthorComment && !isAnnotationAuthor && (
                                            <AnnotatorTag
                                                secondTag={isPostAuthor}
                                            >
                                                Annotator
                                            </AnnotatorTag>
                                        )}
                                    </AnnotationAuthorName>
                                )}
                                {!annotationAuthor && !hideUserSignature && (
                                    <PlaceholderBox
                                        width={ANNOTATION_AUTHOR_PLACEHOLDER_WIDTH}
                                        height={ANNOTATION_AUTHOR_FONT_MULTIPLIER * BODY_FONT_SIZE}
                                    />
                                )}
                                <AnnotationMetadata
                                    isComment={!!annotation.comment && annotationIsComment}
                                    isAuthor={isAnnotationAuthor}
                                >
                                    <Timestamp>
                                        {`${moment.utc(annotation.published).fromNow()}${annotation.history.length > 1 ? ' (edited)' : ''}`}
                                    </Timestamp>
                                    {!isFocused
                                    && !annotation.comment
                                    && !annotationIsComment
                                    && commentCount != null
                                    && commentCount > 0
                                    && (
                                        <AnnotationDetailSeparator>
                                        ·
                                        </AnnotationDetailSeparator>
                                    )}
                                    {!isFocused
                                    && !annotation.comment
                                    && !annotationIsComment
                                    && commentCount != null
                                    && commentCount > 0
                                    && (
                                        <CommentCountContainer>
                                            <CommentCountIcon>
                                                <ReactSVG
                                                    src={CommentIcon}
                                                />
                                            </CommentCountIcon>
                                            <CommentCountText>
                                                {`${formatNumber(commentCount)} ${unreadNotifications && unreadNotifications.length > 0
                                                    ? `(${unreadNotifications.length} new)`
                                                    : ''
                                                }`}
                                            </CommentCountText>
                                        </CommentCountContainer>
                                    )}
                                </AnnotationMetadata>
                            </AnnotationDetails>
                        )}
                    </UserSignature>
                    {(
                        viewportDimensions.width >= MEDIA_QUERY_SIZE.medium.min
                        || isFocused
                        || (
                            !!annotation.comment
                            && annotationIsComment
                        )
                    ) && (
                        <AnnotationEditorContainer
                            isFocused={isFocused}
                            isEditing={isEditing}
                            isComment={!!annotation.comment && annotationIsComment}
                            isAuthor={isAnnotationAuthor}
                            marginLength={MORE_BUTTON_X_OFFSET + ANNOTATION_MORE_BUTTON_LENGTH}
                            paddingLeft={isEditing ? 0 : ANNOTATION_EDITOR_CONTAINER_PADDING_LEFT}
                            transitionDuration={0}
                        >
                            <AnnotationEditor
                                customCursorActive={customCursorActive}
                                annotationIsFocused={isFocused}
                                shouldFocus={isEditing}
                                readOnly={!isEditing}
                                isEditing={isEditing}
                                width={isEditing
                                    ? bucketWidth
                                    : bucketWidth - ANNOTATION_EDITOR_CONTAINER_PADDING_LEFT}
                                user={user}
                                userIsAdmin={userIsAdmin}
                                userEmailVerified={userEmailVerified}
                                currentSessionId={currentSessionId}
                                currentZoomLevel={currentZoomLevel}
                                {...(isAnnotationAuthor && !!annotation.comment && annotationIsComment
                                    ? {
                                        backgroundColor: isEditing
                                            ? themeObj.verascopeColor.purple500
                                            : themeObj.verascopeColor.purple400,
                                        highlightColor: themeObj.verascopeColor.orange200,
                                    }
                                    : {}
                                )}
                                {...(!isAnnotationAuthor && !!annotation.comment && annotationIsComment
                                    ? { backgroundColor: themeObj.color.neutral300 }
                                    : {}
                                )}
                                isComment={!!annotation.comment && annotationIsComment}
                                boxShadow={false}
                                isAuthor={isAnnotationAuthor}
                                hasSound={hasSound}
                                postId={annotation.postId}
                                color={DEFAULT_EDITOR_COLOR}
                                fontMultiplier={ANNOTATION_EDITOR_FONT_MULTIPLIER_REGULAR}
                                interactionDisabled={interactionDisabled}
                                id={annotation.id}
                                type={EDITOR_CONTEXT_TYPE.annotation} // Used to know which storage bucket to save media
                                message={annotation.history[0]}
                                transcript={activeAnnotationTranscript}
                                dictationPlaying={dictationPlaying}
                                timeElapsed={timeElapsed}
                                onCursorEnter={onCursorEnter}
                                onCursorLeave={onCursorLeave}
                                setInputFocused={setInputFocused}
                                setEditor={setAnnotationEditor}
                                setSnackbarData={setSnackbarData}
                                remeasureParentHeight={remeasureParentHeight}
                                editAnnotation={handleEditAnnotation}
                                cancelEditAnnotation={handleCancelEditAnnotation}
                                {...(applyOnMouseEnter ? {
                                    onHoverEnter: onMouseEnter,
                                } : {}
                                )}
                            />
                            {annotationIsDetached
                            && (
                                <DetachedAnnotationNotice
                                    data-annotation-interactive="true"
                                >
                                    <DetachedAnnotationNoticeHeader>
                                        <DetachedAnnotationNoticeIcon src={InfoIcon} />
                                        <DetachedAnnotationNoticeCopy>
                                            {detachedAnnotationNoticeText}
                                        </DetachedAnnotationNoticeCopy>
                                    </DetachedAnnotationNoticeHeader>
                                    {detachedAnnotationPendingReattachPreviewText && (
                                        <DetachedAnnotationNoticePreview>
                                            <DetachedAnnotationNoticePreviewText>
                                                {detachedAnnotationPendingReattachPreviewText}
                                            </DetachedAnnotationNoticePreviewText>
                                        </DetachedAnnotationNoticePreview>
                                    )}
                                    {canRepairDetachedAnnotation && (
                                        <DetachedAnnotationNoticeActions>
                                            {annotationHasPendingDetachedReattachQuote ? (
                                                <>
                                                    <Button
                                                        className={HOVER_TARGET_CLASSNAME}
                                                        customCursorActive={customCursorActive}
                                                        type={BUTTON_TYPE.solid}
                                                        height={1.75 * BODY_FONT_SIZE}
                                                        icon={SaveIcon}
                                                        loading={annotationDetachedReattachInFlight}
                                                        disabled={annotationDetachedReattachInFlight}
                                                        background={themeObj.verascopeColor.orange200}
                                                        tooltip={{
                                                            active: true,
                                                            text: 'Confirm',
                                                            side: TOOLTIP_TYPE.top,
                                                        }}
                                                        {...getTouchMouseEventHandlers({
                                                            onMouseEnter: onButtonMouseEnter,
                                                            onMouseLeave: onButtonMouseLeave,
                                                        })}
                                                        {...getPressEventHandlers({
                                                            onMouseDown: (e) => {
                                                                handleDetachedAnnotationActionPress(e);
                                                                handleConfirmDetachedAnnotationReattach();
                                                            },
                                                        })}
                                                    />
                                                    <Button
                                                        className={HOVER_TARGET_CLASSNAME}
                                                        customCursorActive={customCursorActive}
                                                        type={BUTTON_TYPE.solid}
                                                        height={1.75 * BODY_FONT_SIZE}
                                                        icon={RedoIcon}
                                                        disabled={annotationDetachedReattachInFlight}
                                                        background={themeObj.verascopeColor.purple300}
                                                        {...getTouchMouseEventHandlers({
                                                            onMouseEnter: onButtonMouseEnter,
                                                            onMouseLeave: onButtonMouseLeave,
                                                        })}
                                                        tooltip={{
                                                            active: true,
                                                            text: 'Reselect',
                                                            side: TOOLTIP_TYPE.top,
                                                        }}
                                                        {...getPressEventHandlers({
                                                            onMouseDown: (e) => {
                                                                handleDetachedAnnotationActionPress(e);
                                                                clearDetachedAnnotationPendingQuote!();
                                                            },
                                                        })}
                                                    />
                                                    <Button
                                                        className={HOVER_TARGET_CLASSNAME}
                                                        customCursorActive={customCursorActive}
                                                        type={BUTTON_TYPE.solid}
                                                        height={1.75 * BODY_FONT_SIZE}
                                                        icon={TrashIcon}
                                                        disabled={annotationDetachedReattachInFlight}
                                                        background={themeObj.verascopeColor.purple300}
                                                        tooltip={{
                                                            active: true,
                                                            text: 'Cancel',
                                                            side: TOOLTIP_TYPE.top,
                                                        }}
                                                        {...getTouchMouseEventHandlers({
                                                            onMouseEnter: onButtonMouseEnter,
                                                            onMouseLeave: onButtonMouseLeave,
                                                        })}
                                                        {...getPressEventHandlers({
                                                            onMouseDown: (e) => {
                                                                handleDetachedAnnotationActionPress(e);
                                                                cancelDetachedAnnotationReattach!();
                                                            },
                                                        })}
                                                    />
                                                </>
                                            ) : (
                                                <Button
                                                    className={HOVER_TARGET_CLASSNAME}
                                                    customCursorActive={customCursorActive}
                                                    type={BUTTON_TYPE.solid}
                                                    height={1.75 * BODY_FONT_SIZE}
                                                    text={annotationIsDetachedReattachTarget ? 'Cancel' : 'Reattach'}
                                                    loading={annotationDetachedReattachInFlight}
                                                    disabled={annotationDetachedReattachInFlight}
                                                    background={annotationIsDetachedReattachTarget
                                                        ? themeObj.verascopeColor.purple300
                                                        : themeObj.verascopeColor.orange200}
                                                    {...getTouchMouseEventHandlers({
                                                        onMouseEnter: onButtonMouseEnter,
                                                        onMouseLeave: onButtonMouseLeave,
                                                    })}
                                                    {...getPressEventHandlers({
                                                        onMouseDown: (e) => {
                                                            handleDetachedAnnotationActionPress(e);
                                                            if (annotationIsDetachedReattachTarget) {
                                                                cancelDetachedAnnotationReattach!();
                                                                return;
                                                            }

                                                            startDetachedAnnotationReattach!(annotation);
                                                        },
                                                    })}
                                                />
                                            )}
                                        </DetachedAnnotationNoticeActions>
                                    )}
                                </DetachedAnnotationNotice>
                            )}
                            {canPlayAnnotationDictation
                            && !isEditing
                            && (
                                <DictationButtonContainer
                                    left={DICTATION_BUTTON_X_OFFSET}
                                    length={DICTATION_BUTTON_LENGTH}
                                    isComment={!!annotation.comment && annotationIsComment}
                                    data-annotation-interactive="true"
                                >
                                    <Button
                                        className={HOVER_TARGET_CLASSNAME}
                                        customCursorActive={customCursorActive}
                                        type={BUTTON_TYPE.secret}
                                        height={DICTATION_BUTTON_LENGTH}
                                        width={DICTATION_BUTTON_LENGTH}
                                        icon={DictationIcon}
                                        loading={fetchingDictationAudio}
                                        disabled={!audioURL && fetchingDictationAudio}
                                        {...getTouchMouseEventHandlers({
                                            onMouseEnter: onButtonMouseEnter,
                                            onMouseLeave: onButtonMouseLeave,
                                        })}
                                        {...getPressEventHandlers({ onMouseDown: toggleDictation })}
                                    />
                                </DictationButtonContainer>
                            )}
                        </AnnotationEditorContainer>
                    )}
                    {
                        !isFocused
                        && annotationHeight != null
                        && annotationHeight > MAX_ANNOTATION_HEIGHT // Will never be possible on small viewport
                        && annotationStackActive
                        && !annotationStackExpanded
                        && (
                            !annotationIsComment
                            && !annotation.comment
                        )
                        && (
                            <AnnotationGradient />
                        )
                    }
                    {
                        !isFocused
                        && annotationHeight != null
                        && annotationHeight > MAX_ANNOTATION_HEIGHT // Will never be possible on small viewport
                        && annotationStackActive
                        && !annotationStackExpanded
                        && (
                            !annotationIsComment
                            && !annotation.comment
                        )
                        && (
                            <TruncatedAnnotationIndicator>
                                <ReactSVG
                                    src={MoreIcon}
                                />
                            </TruncatedAnnotationIndicator>
                        )
                    }
                    <AudioElement
                        ref={audioNodeRef}
                        preload="none"
                    >
                        {audioURL
                        && (
                            <AudioSource
                                src={audioURL}
                            />
                        )}
                        <AudioTrack kind="captions" />
                        Your browser does not support audio.
                    </AudioElement>
                </AnnotationBody>
                {isFocused && (
                    (
                        !annotation.comment
                        && !annotationIsComment
                        && fetchingAnnotationComments
                    )
                    || sortedComments.length > 0
                ) && (
                    <AnnotationCommentsContainer>
                        {(fetchingAnnotationComments
                        && annotationComments.size === 0)
                        && displayedSortedComments.length === 0 ? (
                                <SpinnerContainer>
                                    <Spinner
                                        lightBackground
                                    />
                                </SpinnerContainer>
                            ) : (
                                <>
                                    {displayedSortedComments.map((comment, i) => {
                                        const commentAuthor = postUserItems.get(comment.userId);
                                        const isAuthor = !!user && comment.userId === user.id;
                                        return (
                                            <AnnotationCommentContainer
                                                key={comment.id}
                                                isAuthor={isAuthor}
                                                isEditing={editingCommentId === comment.id}
                                            >
                                                {!isAnnotationCommentTombstone(comment)
                                                    ? (
                                                        <Annotation
                                                            customCursorActive={customCursorActive}
                                                            annotation={comment}
                                                            isAnnotationAuthorComment={comment.userId === annotation.userId}
                                                            hasSound={hasSound}
                                                            user={user || null}
                                                            userIsAdmin={userIsAdmin}
                                                            userEmailVerified={userEmailVerified}
                                                            post={post}
                                                            // eslint-disable-next-line max-len
                                                            bucketWidth={bucketWidth - 2 * horizontalMargin - 2 * ANNOTATION_COMMENTS_CONTAINER_VERTICAL_MARGIN}
                                                            currentSessionId={currentSessionId}
                                                            annotationStackActive={annotationStackActive}
                                                            index={i}
                                                            horizontalMargin={0}
                                                            annotationCount={displayedSortedComments.length}
                                                            hide={false}
                                                            interactionDisabled={interactionDisabled}
                                                            hideUserSignature={i + 1 < displayedSortedComments.length
                                                                && !isAnnotationCommentTombstone(displayedSortedComments[i + 1])
                                                                && displayedSortedComments[i + 1].userId === comment.userId}
                                                            annotationStackExpanded={false}
                                                            bucketHeight={bucketHeight}
                                                            viewportDimensions={viewportDimensions}
                                                            postAuthorIds={postAuthorIds}
                                                            postUserItems={postUserItems}
                                                            postUserAvatarURLs={postUserAvatarURLs}
                                                            annotationAudioURLs={annotationAudioURLs}
                                                            onCursorEnter={onCursorEnter}
                                                            onCursorLeave={onCursorLeave}
                                                            setInputFocused={setInputFocused}
                                                            setCursorSigns={setCursorSigns}
                                                            setSnackbarData={setSnackbarData}
                                                            setRemeasureBucketHeight={setRemeasureBucketHeight}
                                                            clearTimeoutAnnotationNudge={clearTimeoutAnnotationNudge}
                                                            selectedPostValuePath={selectedPostValuePath}
                                                            currentZoomLevel={currentZoomLevel}
                                                            handleEditComment={(id: string | null) => setEditingCommentId(id)}
                                                            setRefreshNotifications={setRefreshNotifications}
                                                            removeAnnotationLocally={markCommentDeletedLocally}
                                                            refreshAnnotation={refreshComment}
                                                            addToPostUserItems={addToPostUserItems}
                                                            addToAnnotationAudioURLs={addToAnnotationAudioURLs}
                                                            removeFromAnnotationAudioURLs={removeFromAnnotationAudioURLs}
                                                        />
                                                    ) : (
                                                        <DeletedCommentContainer
                                                            isAuthor={isAuthor}
                                                            marginLength={MORE_BUTTON_X_OFFSET + ANNOTATION_MORE_BUTTON_LENGTH}
                                                        >
                                                            {commentAuthor
                                                                ? `${!!user && commentAuthor.id === user.id
                                                                    ? 'You'
                                                                    : `${commentAuthor.firstName}`
                                                                } deleted a comment`
                                                                : 'User deleted a comment'}
                                                        </DeletedCommentContainer>
                                                    )}
                                            </AnnotationCommentContainer>
                                        );
                                    })}
                                </>
                            )}
                    </AnnotationCommentsContainer>
                )}
            </ScrollContainer>
            {isFocused && (
                <AnnotationCommentEditorContainer
                    data-annotation-interactive="true"
                >
                    <AnnotationEditor
                        id={`annotation-comment-editor-id=${annotation.id}`}
                        customCursorActive={customCursorActive}
                        user={user}
                        userIsAdmin={userIsAdmin}
                        userEmailVerified={userEmailVerified}
                        shouldFocus
                        isCommentEditor
                        currentSessionId={currentSessionId}
                        currentZoomLevel={currentZoomLevel}
                        readOnly={false}
                        hasSound={hasSound}
                        postId={annotation.postId}
                        width={bucketWidth - 2 * horizontalMargin - 2 * ANNOTATION_COMMENT_EDITOR_CONTAINER_PADDING}
                        type={EDITOR_CONTEXT_TYPE.annotation} // Used to know which storage bucket to save media
                        color={DEFAULT_EDITOR_COLOR}
                        fontMultiplier={ANNOTATION_EDITOR_FONT_MULTIPLIER_REGULAR}
                        interactionDisabled={interactionDisabled}
                        submitAnnotationComment={handleSubmitAnnotationComment}
                        placeholder="Comment..."
                        onCursorEnter={onCursorEnter}
                        onCursorLeave={onCursorLeave}
                        setInputFocused={setInputFocused}
                        viewportDimensions={viewportDimensions}
                        setEditor={setAnnotationCommentEditor}
                        setSnackbarData={setSnackbarData}
                        setIsUserProfileDialogExpanded={setIsUserProfileDialogExpanded}
                        setNotifyUserToSignUpAfterAnnotationCreationAttempt={setNotifyUserToSignUpAfterAnnotationCreationAttempt}
                    />
                </AnnotationCommentEditorContainer>
            )}
        </Container>
    ), [
        // ===== Props =====
        annotation.history,
        annotation.dictationFilePaths,
        annotation.transcripts,
        annotation.quoteHistory,
        annotation.detached,
        annotation.deleted,
        sortedComments,
        post?.title,
        hasSound,
        userEmailVerified,
        userIsAdmin,
        bucketWidth,
        zIndex,
        annotationStackActive,
        annotationStackExpanded,
        hide,
        interactionDisabled,
        viewportDimensions,
        annotationEditor,
        annotationCommentEditor,
        postUserItems,
        postAuthorIds,
        postUserAvatarURLs,
        index,
        // You might think it makes sense to use annotationAudioURLs.get(annotation.id)
        // This is a bad idea because its child comments won't get their audio urls
        annotationAudioURLs,
        customCursorActive,
        // ===== State =====
        isEditing,
        editingCommentId,
        annotationHeight,
        priorAnnotationsHeight,
        isFocused,
        dictationPlaying,
        timeElapsed,
        annotationComments,
        modal,
        optionsMenuIsVisible,
        detachedAnnotationReattachTargetId,
        detachedAnnotationReattachInFlightId,
        fetchingAnnotationComments,
        fetchingDictationAudio,
        lastFetchedDictationReadyKey,
        // ===== Memoizations =====
        // isAnnotationAuthor,
        // isPostAuthor,
        // hasAuthorAvatarImage,
        DictationIcon,
        canPlayAnnotationDictation,
        // OptionsMenuButtons,
        sortedComments,
        commentCount,
        annotationAuthorAvatarImageURL,
        postAuthorAvatarImageURL,
        annotationAuthor,
        audioURL,
        unreadNotifications,
        containerPressHandlers,
        startDetachedAnnotationReattach,
        cancelDetachedAnnotationReattach,
    ]);

    return AnnotationComponent;
}

// ===== Styled Components =====

interface ContainerProps {
    index: number,
    zIndex?: number,
    customCursorActive: boolean,
    isFocused: boolean,
    isComment: boolean,
    hide: boolean,
    priorAnnotationsHeight: number,
    annotationStackActive: boolean,
    annotationStackExpanded: boolean,
    stackOffsetIncrementLength: number,
    horizontalMargin: number,
    transitionDuration: number,
}
const Container = styled.div<ContainerProps>`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    position: ${({ hide }) => (hide ? 'absolute' : 'relative')};
    top: ${({
        hide,
        index,
        priorAnnotationsHeight,
        annotationStackActive,
        annotationStackExpanded,
        stackOffsetIncrementLength,
        isComment,
    }) => {
        if (annotationStackActive && !annotationStackExpanded && !hide && !isComment) {
            return `${-priorAnnotationsHeight + index * stackOffsetIncrementLength}px`;
        }

        return '0px';
    }};
    left: 0px;
    width: ${({ horizontalMargin }) => `calc(100% - ${2 * horizontalMargin}px)`};
    height: ${({ isFocused, isComment }) => (isFocused && !isComment
        ? '100%'
        : 'auto'
    )};
    max-width: ${({ isFocused, isComment }) => (isFocused || isComment
        ? 'none'
        : `${31.25 * BODY_FONT_SIZE}px`
    )};
    border-radius: ${`${0.3125 * BODY_FONT_SIZE}px`};
    margin: ${({ horizontalMargin }) => `0px ${horizontalMargin}px`};
    margin-top: ${({
        index,
        isFocused,
        isComment,
        annotationStackActive,
        annotationStackExpanded,
    }) => {
        if (
            index > 0
            && (
                (
                    !annotationStackActive
                    && !annotationStackExpanded
                    // When focused, the annotation is absolutely positioned to be top of annotation bucket
                    && !isFocused
                )
            ) && !isComment
        ) {
            return `${0.625 * BODY_FONT_SIZE}px`;
        }
        return '0px';
    }};
    background: ${({ theme }) => theme.color.white};
    box-shadow: ${({ isComment, theme }) => (isComment
        ? 'none'
        : theme.color.boxShadow100
    )};
    z-index: ${({ zIndex }) => (zIndex || 'auto')};
    pointer-events: ${({ hide }) => (hide ? 'none' : 'auto')};
    visibility: ${({ hide }) => (hide ? 'hidden' : 'visible')};
    cursor: ${({ customCursorActive, isFocused }) => (customCursorActive || isFocused ? 'default' : 'pointer')};
    &::selection,
    & *::selection {
        background: Highlight;
        color: HighlightText;
    }
    &::-moz-selection,
    & *::-moz-selection {
        background: Highlight;
        color: HighlightText;
    }
    transition: ${({
        theme,
        annotationStackActive,
        transitionDuration,
    }) => annotationStackActive && `
        top ${transitionDuration}ms ${theme.motion.eagerEasing}
    `};

    ${({ isComment, theme }) => !isComment && `
        &:hover {
            box-shadow: ${theme.color.boxShadow300};
        }
    `}

    ${({ theme }) => theme.mediaQuery.small} {
        ${({ isFocused }) => isFocused && `
            box-shadow: none;

            &:hover {
                box-shadow: none;
            }
        `}
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        ${({ isFocused }) => isFocused && `
            box-shadow: none;

            &:hover {
                box-shadow: none;
            }
        `}
    }
`;

interface ScrollContainerProps {
    isEditing: boolean,
}
const ScrollContainer = styled.div<ScrollContainerProps>`
    width: 100%;
    overflow-y: ${({ isEditing }) => (isEditing ? 'visible' : 'scroll')};
`;

interface AnnotationBodyProps {
    isFocused: boolean,
    isComment: boolean,
    isAuthor: boolean,
    isEditing: boolean,
    maxHeight?: number,
    annotationStackActive: boolean,
    annotationStackExpanded: boolean,
}
const AnnotationBody = styled.div<AnnotationBodyProps>`
    display: flex;
    flex-direction: ${({ isFocused, isComment }) => (isFocused || isComment
        ? 'column-reverse'
        : 'column'
    )};
    align-items: ${({ isComment, isAuthor }) => {
        if (isComment && isAuthor) {
            return 'flex-end';
        }
        if (isComment) {
            return 'flex-start';
        }
        return 'unset';
    }};
    border-radius: ${`${0.3125 * BODY_FONT_SIZE}px`};
    max-height: ${({
        maxHeight,
        annotationStackActive,
        annotationStackExpanded,
    }) => (annotationStackActive && !annotationStackExpanded && maxHeight != null ? `${maxHeight + 1.25 * BODY_FONT_SIZE}px` : 'none')};
    overflow-x: ${({ isEditing }) => {
        if (isEditing) return 'visible';
        return 'hidden';
    }};
    overflow-y: ${({ isFocused, isEditing }) => {
        if (isEditing) return 'visible';
        if (isFocused) return 'scroll';
        return 'hidden';
    }};
`;

interface UserSignatureProps {
    isFocused: boolean,
    isComment: boolean,
    isAuthor: boolean,
    hideUserSignature?: boolean,
    padding: number,
    marginLength?: number,
    transitionDuration: number,
}
const UserSignature = styled.div<UserSignatureProps>`
    position: relative;
    display: flex;
    flex-direction: ${({ isComment, isAuthor }) => (isComment && isAuthor
        ? 'row-reverse'
        : 'row'
    )};
    align-items: center;
    margin-left: ${({
        isComment,
        marginLength,
        isAuthor,
        hideUserSignature,
    }) => `${isComment
    && marginLength != null && !isAuthor && hideUserSignature
        ? marginLength
        : 0}px`};
    padding: ${({ padding }) => `${padding}px`};
    padding-bottom: ${`${0.0625 * BODY_FONT_SIZE}px`};
    padding-top: ${({
        isFocused,
        isComment,
        hideUserSignature,
        padding,
    }) => {
        if (isFocused) {
            return '0px';
        }
        if (isComment && hideUserSignature) {
            return `${0.0625 * BODY_FONT_SIZE}px`;
        }

        return `${padding}px`;
    }};
    padding-left: ${({ isComment, isAuthor, padding }) => `${isComment && !isAuthor ? 0 : padding}px`};
    padding-right: ${({ isComment, isAuthor, padding }) => `${isComment && isAuthor ? 0 : padding}px`};
    transition: ${({ transitionDuration, theme }) => `
        padding-top ${transitionDuration}ms ${theme.motion.eagerEasing}
    `};

    ${({ theme }) => theme.mediaQuery.small} {
        padding-bottom: ${({ padding }) => `${padding}px`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        padding-bottom: ${({ padding }) => `${padding}px`};
    }
`;

interface AnnotationDetailsProps {
    isComment: boolean,
    isAuthor: boolean,
}
const AnnotationDetails = styled.div<AnnotationDetailsProps>`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    margin-left: ${({ isComment, isAuthor }) => `${isComment && isAuthor ? 'auto' : `${0.5 * BODY_FONT_SIZE}px`}`};
    margin-right: ${({ isComment, isAuthor }) => `${isComment && isAuthor ? `${0.5 * BODY_FONT_SIZE}px` : 'auto'}`};
`;

interface AnnotationMetadataProps {
    isComment: boolean,
    isAuthor: boolean,
}
const AnnotationMetadata = styled.div<AnnotationMetadataProps>`
    display: flex;
    flex-direction: row;
    justify-content: ${({ isComment, isAuthor }) => (isComment && isAuthor ? 'flex-end' : 'flex-start')};
`;

interface AnnotationAuthorNameProps {
    fontMultiplier: number,
}
const AnnotationAuthorName = styled.h4<AnnotationAuthorNameProps>`
    position: relative;
    display: inline-flex;
    flex-direction: row;
    opacity: 0.87;
    margin: 0;
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: ${({ fontMultiplier }) => `${fontMultiplier}em`};
    font-weight: 600;
    color: ${({ theme }) => theme.color.neutral1000};
    line-height: 1.2em;
    text-overflow: ellipsis;
`;

const Timestamp = styled.span`
    display: inline-block;
    font-size: 0.7em;
    line-height: 1.5em;
    color: ${({ theme }) => theme.color.neutral700};
    white-space: nowrap;
`;

const AnnotationDetailSeparator = styled.span`
    margin: ${`0px ${0.15625 * BODY_FONT_SIZE}px`};
    font-size: 0.7em;
    line-height: 1.5em;
    color: ${({ theme }) => theme.color.neutral700};
`;

const CommentCountContainer = styled.span`
    display: flex;
    align-items: center;
    z-index: ${ANNOTATION_EDITOR_Z_INDEX};
`;

const CommentCountIcon = styled.div`
    position: relative;
    top: ${`${0.03125 * BODY_FONT_SIZE}px`};
    height: ${`${1.125 * BODY_FONT_SIZE}px`};
    width: ${`${1.125 * BODY_FONT_SIZE}px`};

    & path {
        fill: ${({ theme }) => theme.color.neutral600};
    }
`;

const CommentCountText = styled.h3`
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-weight: 500;
    font-size: 0.7em;
    line-height: 1em;
    color: ${({ theme }) => theme.color.neutral800};
    margin: 0;
    margin-left: ${`${0.15625 * BODY_FONT_SIZE}px`};
`;

interface OptionsMenuContainerProps {
    length: number,
    isComment: boolean,
    isAuthor: boolean,
    xOffset: number,
}
const OptionsMenuContainer = styled.div<OptionsMenuContainerProps>`
    position: absolute;
    top: ${`${0.15625 * BODY_FONT_SIZE}px`};
    right: ${({ isComment, isAuthor, xOffset }) => {
        if (isComment && isAuthor) {
            return 'auto';
        }
        return `${xOffset}px`;
    }};
    left: ${({ isComment, isAuthor, xOffset }) => {
        if (isComment && isAuthor) {
            return `${xOffset}px`;
        }
        return 'auto';
    }};
    background: ${({ theme }) => theme.color.white};
    border-radius: ${({ length }) => `${length / 2}px`};
    z-index: ${({ isComment }) => (!isComment ? ANNOTATION_EDITOR_Z_INDEX + 1 : 1)};

    & > div {
        background: ${({ theme }) => theme.color.white};
    }

    &:hover {
        z-index: ${ANNOTATION_EDITOR_Z_INDEX + 1};
    }

    ${({ isComment, theme }) => !isComment && `
        ${theme.mediaQuery.small} {
        top: ${`${MODAL_SHEET_CLOSE_BUTTON_CONTAINER_TOP}px`};
        right: ${3.125 * BODY_FONT_SIZE}px;
        background: ${MODAL_SHEET_CLOSE_BUTTON_COLOR};
    }
        ${theme.mediaQuery.extraSmall} {
        top: ${`${MODAL_SHEET_CLOSE_BUTTON_CONTAINER_TOP}px`};
        right: ${3.125 * BODY_FONT_SIZE}px;
        background: ${MODAL_SHEET_CLOSE_BUTTON_COLOR};
    }
    `}
`;

const AuthorTag = styled.span`
    background: ${({ theme }) => theme.verascopeColor.purple400};
    border-radius: ${`${0.46875 * BODY_FONT_SIZE}px`};
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-weight: 500;
    font-size: 0.7em;
    color: ${({ theme }) => fontColorDiscriminator(theme.verascopeColor.purple400)};
    line-height: 1em;
    padding: ${`${0.15625 * BODY_FONT_SIZE}px ${0.46875 * BODY_FONT_SIZE}px`};
    margin-left: ${`${0.46875 * BODY_FONT_SIZE}px`};
    height: fit-content;
`;

interface AnnotatorTagProps {
    secondTag: boolean,
}
const AnnotatorTag = styled.span<AnnotatorTagProps>`
    background: ${({ theme }) => theme.verascopeColor.orange200};
    border-radius: ${`${0.46875 * BODY_FONT_SIZE}px`};
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-weight: 500;
    font-size: 0.7em;
    color: ${({ theme }) => fontColorDiscriminator(theme.verascopeColor.orange200)};
    line-height: 1em;
    padding: ${`${0.15625 * BODY_FONT_SIZE}px ${0.46875 * BODY_FONT_SIZE}px`};
    margin-left: ${({ secondTag }) => `${secondTag ? 0.3125 * BODY_FONT_SIZE : 0.46875 * BODY_FONT_SIZE}px`};
    height: fit-content;
`;

interface CloseButtonContainerProps {
    buttonHeight: number,
}
const CloseButtonContainer = styled.div<CloseButtonContainerProps>`
    position: absolute;
    top: ${({ buttonHeight }) => `-${buttonHeight + 0.3125 * BODY_FONT_SIZE}px`};
    left: 0px;
    background: ${({ theme }) => theme.color.white};
    border-radius: ${({ buttonHeight }) => `${buttonHeight / 2}px`};
`;

interface QuoteTextProps {
    transitionDuration: number,
}
const QuoteText = styled.p<QuoteTextProps>`
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-weight: 600;
    font-size: 0.9em;
    line-height: 1em;
    color: ${({ theme }) => theme.color.neutral1000};
    margin: 0;
    margin-bottom: ${`${0.9375 * BODY_FONT_SIZE}px`};
    padding-left: ${`${0.3125 * BODY_FONT_SIZE}px`};
    border-left: ${({ theme }) => `${0.25 * BODY_FONT_SIZE}px solid ${theme.color.neutral700}`};
    transition: ${({ transitionDuration, theme }) => `
        border ${transitionDuration}ms ${theme.motion.eagerEasing}
    `};
`;

interface QuoteContainerProps {
    marginTop: number,
    transitionDuration: number,
    interactive: boolean,
}
const QuoteContainer = styled.div<QuoteContainerProps>`
    margin: ${`${0.9375 * BODY_FONT_SIZE}px`};
    margin-top: ${({ marginTop }) => `${marginTop}px`};
    margin-bottom: ${`${0.3125 * BODY_FONT_SIZE}px`};
    margin-left: ${`${2.5 * BODY_FONT_SIZE}px`};
    padding: ${`${0.625 * BODY_FONT_SIZE}px`};
    border: ${({ theme }) => `${0.125 * BODY_FONT_SIZE}px solid ${theme.color.neutral300}`};
    border-radius: ${`${0.3125 * BODY_FONT_SIZE}px`};
    cursor: ${({ interactive }) => (interactive ? 'pointer' : 'default')};
    transition: ${({ transitionDuration, theme }) => `
        border ${transitionDuration}ms ${theme.motion.eagerEasing}
    `};

    ${({ interactive, theme }) => interactive && `
        &:hover {
            border: ${0.125 * BODY_FONT_SIZE}px solid ${theme.verascopeColor.purple400};

            & > ${QuoteText} {
                border-left: ${0.25 * BODY_FONT_SIZE}px solid ${theme.verascopeColor.purple400};
            }
        }
    `}
`;

const PostAuthorSignature = styled.div`
    display: flex;
    flex-direction: row-reverse;
    justify-content: flex-start;
    align-items: center;
`;

const PostAuthorDetails = styled.div`
    margin-right: ${`${0.46875 * BODY_FONT_SIZE}px`};
`;

const PostAuthorName = styled.p`
    position: relative;
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-weight: 400;
    font-size: 0.8em;
    line-height: 1em;
    color: ${({ theme }) => theme.color.neutral1000};
    margin: 0;
    margin-bottom: ${`${0.3125 * BODY_FONT_SIZE}px`};
    text-align: right;
`;

const PostAuthorPostName = styled.p`
    position: relative;
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-weight: 500;
    font-size: 0.7em;
    line-height: 1em;
    color: ${({ theme }) => theme.color.neutral600};
    text-align: right;
    margin: 0;
`;

interface AnnotationEditorContainerProps {
    isFocused: boolean,
    transitionDuration: number,
    paddingLeft: number,
    isComment: boolean,
    isAuthor: boolean,
    isEditing: boolean,
    marginLength?: number,
}
const AnnotationEditorContainer = styled.div<AnnotationEditorContainerProps>`
    position: relative;
    padding-left: ${({ paddingLeft }) => `${paddingLeft}px`};
    margin-left: ${({
        isComment,
        marginLength,
        isAuthor,
        isEditing,
    }) => `${isComment
        && marginLength != null && isAuthor && !isEditing
        ? marginLength
        : 0}px`};
    margin-bottom: ${({ isFocused }) => `${isFocused ? 0.3125 * BODY_FONT_SIZE : 0}px`};
    transition: ${({ transitionDuration, theme }) => `
        padding-top ${transitionDuration}ms ${theme.motion.eagerEasing},
        margin-bottom ${transitionDuration}ms ${theme.motion.eagerEasing}
    `};
`;

const DetachedAnnotationNotice = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: ${`${0.5 * BODY_FONT_SIZE}px`};
    margin: ${`${0.5 * BODY_FONT_SIZE}px`};
    padding: ${`${0.5 * BODY_FONT_SIZE}px`};
    border-radius: ${`${0.3125 * BODY_FONT_SIZE}px`};
    background: ${({ theme }) => theme.verascopeColor.purple400};
    box-shadow: ${({ theme }) => theme.color.boxShadow100};
`;

const DetachedAnnotationNoticeHeader = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    column-gap: ${`${0.5 * BODY_FONT_SIZE}px`};
`;

const DetachedAnnotationNoticeIcon = styled(ReactSVG)`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: ${`${0.75 * BODY_FONT_SIZE}px`};
    height: ${`${0.75 * BODY_FONT_SIZE}px`};
    margin-top: ${`${0.125 * BODY_FONT_SIZE}px`};

    & > div {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
    }

    & svg {
        width: 100%;
        height: 100%;
        fill: ${({ theme }) => theme.color.white};
    }
`;

const DetachedAnnotationNoticeCopy = styled.p`
    flex: 1 1 auto;
    margin: 0;
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: 0.75em;
    line-height: 1.4;
    color: ${({ theme }) => theme.color.white};
`;

const DetachedAnnotationNoticePreview = styled.div`
    width: 100%;
    padding: ${`${0.625 * BODY_FONT_SIZE}px`};
    border: ${({ theme }) => `${0.125 * BODY_FONT_SIZE}px solid ${theme.color.neutral300}`};
    border-radius: ${`${0.3125 * BODY_FONT_SIZE}px`};
    background: ${({ theme }) => theme.color.white};
`;

const DetachedAnnotationNoticePreviewText = styled.p`
    margin: 0;
    padding-left: ${`${0.3125 * BODY_FONT_SIZE}px`};
    border-left: ${({ theme }) => `${0.25 * BODY_FONT_SIZE}px solid ${theme.color.neutral700}`};
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-weight: 600;
    font-size: 0.9em;
    line-height: 1.2;
    color: ${({ theme }) => theme.color.neutral1000};
`;

const DetachedAnnotationNoticeActions = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: ${`${0.375 * BODY_FONT_SIZE}px`};
`;

const TruncatedAnnotationIndicator = styled.div`
    position: absolute;
    bottom: 0px;
    left: 50%;
    transform: translateX(-50%);
    margin-bottom: ${`${0.1875 * BODY_FONT_SIZE}px`};
    height: ${`${1.25 * BODY_FONT_SIZE}px`};
    width: ${`${1.25 * BODY_FONT_SIZE}px`};
    z-index: ${ANNOTATION_EDITOR_Z_INDEX};

    & svg, & div {
        position: relative;
        height: ${`${1.25 * BODY_FONT_SIZE}px`};
        width: ${`${1.25 * BODY_FONT_SIZE}px`};
        margin: 0;
    }

    & path {
        fill: ${({ theme }) => theme.color.neutral800};
    }
`;

const AnnotationGradient = styled.div`
    position: absolute;
    bottom: 0px;
    left: 0px;
    width: 100%;
    height: ${`${3.125 * BODY_FONT_SIZE}px`};
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0px, rgba(255, 255, 255, 1));
    border-radius: ${`${0.3125 * BODY_FONT_SIZE}px`};
    z-index: ${ANNOTATION_EDITOR_Z_INDEX};
`;

interface DictationButtonContainerProps {
    left: number,
    length: number,
    isComment: boolean,
}
const DictationButtonContainer = styled.div<DictationButtonContainerProps>`
    position: absolute;
    bottom: ${({ isComment }) => `${isComment ? 0.125 * BODY_FONT_SIZE : 0.125 * BODY_FONT_SIZE}px`};
    left: ${({ left }) => `${left}px`};
    background: ${({ theme }) => theme.color.white};
    border-radius: ${({ length }) => `${length / 2}px`};
    z-index: ${ANNOTATION_EDITOR_Z_INDEX};
    touch-action: manipulation;
    pointer-events: auto;

    & svg {
        fill: ${({ theme }) => theme.color.neutral700};
    }
`;

const AnnotationCommentEditorContainer = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    padding: ${`${ANNOTATION_COMMENT_EDITOR_CONTAINER_PADDING}px`};
    width: 100%;
    background: ${({ theme }) => theme.color.neutral500};
    border-bottom-left-radius: ${`${0.3125 * BODY_FONT_SIZE}px`};
    border-bottom-right-radius: ${`${0.3125 * BODY_FONT_SIZE}px`};
    z-index: ${ANNOTATION_EDITOR_Z_INDEX};
    touch-action: manipulation;
    pointer-events: auto;
`;

interface AnnotationCommentContainerProps {
    isAuthor: boolean,
    isEditing: boolean,
}
const AnnotationCommentContainer = styled.div<AnnotationCommentContainerProps>`
    position: relative;
    display: flex;
    justify-content: ${({ isAuthor }) => (isAuthor ? 'flex-end' : 'flex-start')};
    margin-bottom: ${`${0.3125 * BODY_FONT_SIZE}px`};
    z-index: ${({ isEditing }) => (isEditing ? ANNOTATION_EDITOR_Z_INDEX + 1 : 'auto')};
`;

const AnnotationCommentsContainer = styled.div`
    margin: ${`0 ${ANNOTATION_COMMENTS_CONTAINER_VERTICAL_MARGIN}px`};
    margin-top: ${`${0.625 * BODY_FONT_SIZE}px`};
    margin-bottom: ${`${7.5 * BODY_FONT_SIZE}px`};
    border-top: ${({ theme }) => `${0.0625 * BODY_FONT_SIZE}px solid ${theme.color.neutral300}`};
    padding-top: ${`${0.625 * BODY_FONT_SIZE}px`};
`;

const SpinnerContainer = styled.div`
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
`;

interface DeletedCommentContainerProps {
    isAuthor: boolean,
    marginLength: number,
}
const DeletedCommentContainer = styled.div<DeletedCommentContainerProps>`
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: 0.8em;
    font-weight: 400;
    color: ${({ theme }) => theme.color.neutral500};
    line-height: 1.2em;
    margin-left: ${({ isAuthor, marginLength }) => `${isAuthor ? 0 : marginLength}px`};
    padding: ${`${0.3125 * BODY_FONT_SIZE}px ${0.82 * BODY_FONT_SIZE}px`};
    border: ${({ theme }) => `${0.0625 * BODY_FONT_SIZE}px solid ${theme.color.neutral300}`};
    width: max-content;
    border-radius: ${`${0.9375 * BODY_FONT_SIZE}px`};
`;

const AudioElement = styled.audio``;
const AudioSource = styled.source``;
const AudioTrack = styled.track``;

export default Annotation;
